<template>
  <div class="flex flex-col h-full">
    <!-- 顶部搜索和筛选栏 -->
    <div class="mb-6 space-y-4">
      <!-- 搜索框 -->
      <div class="flex items-center gap-4">
        <div class="relative flex-1 max-w-md">
          <i class="ph-bold ph-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"></i>
          <input
            v-model="searchQuery"
            @input="handleSearch"
            type="text"
            placeholder="搜索订单号、用户ID、商品名称..."
            class="w-full bg-slate-800/50 border border-slate-700/50 rounded-lg pl-10 pr-10 py-2.5 text-sm text-slate-300 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all placeholder-slate-600"
          />
          <button
            v-if="searchQuery"
            @click="searchQuery = ''; handleSearch()"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
          >
            <i class="ph-bold ph-x text-sm"></i>
          </button>
        </div>
        <div class="text-xs text-slate-500 font-mono whitespace-nowrap">
          共 <span class="text-blue-400 font-bold">{{ filteredOrders.length }}</span> 条记录
          <span class="mx-2">·</span>
          第 <span class="text-purple-400 font-bold">{{ currentPage }}</span> / <span class="text-purple-400 font-bold">{{ totalPages }}</span> 页
        </div>
      </div>

      <!-- 状态筛选按钮 -->
      <div class="flex gap-2 overflow-x-auto pb-2 no-scrollbar">
        <button
          v-for="item in filters"
          :key="item.id"
          @click="currentFilter = item.id"
          class="px-5 py-2.5 rounded-xl text-xs font-bold border transition-all whitespace-nowrap tracking-wider relative overflow-hidden group"
          :class="currentFilter === item.id
            ? 'bg-gradient-to-r from-blue-600/20 to-blue-500/20 text-blue-300 border-blue-500/50 shadow-[0_0_15px_rgba(37,99,235,0.25)]'
            : 'bg-white/5 text-gray-500 border-white/5 hover:bg-white/10 hover:border-white/20 hover:text-gray-300'"
        >
          <span class="relative z-10">{{ item.label }}</span>
          <div v-if="currentFilter === item.id" class="absolute inset-0 bg-gradient-to-r from-blue-600/10 to-transparent animate-pulse"></div>
        </button>
      </div>
    </div>

    <!-- 订单列表 -->
    <div v-if="loading" class="flex items-center justify-center py-24">
      <div class="text-slate-500">加载中...</div>
    </div>

    <div v-else-if="filteredOrders.length === 0" class="flex flex-col items-center justify-center py-24 text-slate-500">
      <i class="ph-bold ph-package text-6xl mb-4 opacity-50"></i>
      <p class="text-lg">暂无订单数据</p>
    </div>

    <div v-else class="overflow-y-auto flex-1">
      <TransitionGroup name="list" tag="div" class="space-y-4" appear>
        <div
          v-for="order in paginatedOrders"
          :key="`order-${order.id}`"
          class="glass-panel rounded-2xl border border-white/5 hover:border-blue-500/30 transition-all duration-300 group hover:shadow-2xl hover:shadow-blue-900/10 overflow-hidden"
        >
          <!-- 订单头部 - 渐变背景 -->
          <div class="bg-gradient-to-r from-[#111827]/90 to-[#1f2937]/90 p-5 flex justify-between items-center border-b border-white/5">
            <div class="flex items-center gap-8">
              <div class="flex flex-col">
                <span class="text-[10px] text-slate-500 uppercase tracking-wider mb-1 font-bold flex items-center gap-1.5">
                  <div class="w-1 h-1 rounded-full bg-blue-500"></div>
                  订单编号
                </span>
                <span class="font-mono text-white font-bold group-hover:text-blue-300 transition-colors tracking-wide">{{ order.orderNumber }}</span>
              </div>
              <div class="h-10 w-px bg-white/10"></div>
              <div class="flex flex-col">
                <span class="text-[10px] text-slate-500 uppercase tracking-wider mb-1 font-bold flex items-center gap-1.5">
                  <div class="w-1 h-1 rounded-full bg-emerald-500"></div>
                  用户ID
                </span>
                <span class="text-slate-300 font-mono text-sm">{{ order.userId }}</span>
              </div>
              <div class="h-10 w-px bg-white/10"></div>
              <div class="flex flex-col">
                <span class="text-[10px] text-slate-500 uppercase tracking-wider mb-1 font-bold flex items-center gap-1.5">
                  <div class="w-1 h-1 rounded-full bg-purple-500"></div>
                  下单时间
                </span>
                <span class="text-slate-300 text-sm">{{ formatDateTime(order.createdAt) }}</span>
              </div>
              <!-- 退款申请提示 -->
              <template v-if="order.refundStatus && order.refundStatus !== 'completed'">
                <div class="h-10 w-px bg-white/10"></div>
                <div class="flex flex-col">
                  <span class="text-[10px] text-orange-500 uppercase tracking-wider mb-1 font-bold flex items-center gap-1.5 animate-pulse">
                    <div class="w-1 h-1 rounded-full bg-orange-500"></div>
                    退款申请
                  </span>
                  <span class="text-orange-300 text-sm font-bold">{{ getRefundStatusLabel(order.refundStatus) }}</span>
                </div>
              </template>
              <!-- 退款成功提示 -->
              <template v-else-if="order.refundStatus === 'completed'">
                <div class="h-10 w-px bg-white/10"></div>
                <div class="flex flex-col">
                  <span class="text-[10px] text-green-500 uppercase tracking-wider mb-1 font-bold flex items-center gap-1.5">
                    <div class="w-1 h-1 rounded-full bg-green-500"></div>
                    退款成功
                  </span>
                  <span class="text-green-300 text-sm font-bold">¥{{ order.refundAmount?.toFixed(2) || '0.00' }}</span>
                </div>
              </template>
            </div>
            <div
              class="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold border shadow-lg"
              :class="statusMap[order.status].color"
            >
              <i :class="statusMap[order.status].icon" class="text-base"></i>
              <span>{{ statusMap[order.status].label }}</span>
            </div>
          </div>

          <!-- 订单内容 - 三栏布局 -->
          <div class="p-5 flex gap-6">
            <!-- 左侧：商品信息 -->
            <div class="flex-1">
              <!-- 主商品和配件的整体容器 -->
              <div class="relative">
                <!-- 主商品 -->
                <div v-if="order.items && order.items.length > 0" class="flex justify-between items-start group/item">
                  <div class="flex items-start gap-4">
                    <div class="relative">
                      <div class="w-12 h-12 bg-gray-800 rounded-xl border-2 border-blue-500/40 overflow-hidden group-hover/item:border-blue-500/70 transition-all flex-shrink-0 shadow-lg shadow-blue-500/20">
                        <img 
                          :src="getProductImage(order.items[0].itemName)" 
                          :alt="order.items[0].itemName"
                          class="w-full h-full object-cover group-hover/item:scale-110 transition-transform duration-300"
                          @error="(e) => e.target.src = '/images/shop/No1.png'"
                        />
                      </div>
                      <!-- 主商品标识 -->
                      <div class="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg">
                        <i class="ph-fill ph-star text-white text-xs"></i>
                      </div>
                    </div>
                    <div class="flex flex-col justify-center">
                      <p class="text-white text-base font-bold group-hover/item:text-blue-300 transition-colors cursor-pointer leading-tight">
                        {{ order.items[0].itemName }}
                      </p>
                      <p class="text-gray-500 text-[11px] mt-1 flex items-center gap-2">
                        <span class="px-1.5 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-blue-400 font-bold text-[9px]">主商品</span>
                        <span>编码: <span class="text-slate-300 font-mono">{{ order.items[0].itemCode }}</span></span>
                        <span>数量: <span class="text-gray-300 font-mono font-bold">{{ order.items[0].quantity }}</span></span>
                      </p>
                    </div>
                  </div>
                  <span class="font-mono text-white font-bold text-base">¥{{ (order.items[0].price || 0).toFixed(2) }}</span>
                </div>
                
                <!-- 配件列表 - 竖线从主商品图片底部中心延伸 -->
                <div v-if="order.items && order.items.length > 1" class="relative mt-0">
                  <!-- 主竖线 - 从主商品图片底部中心开始 -->
                  <div class="absolute left-6 top-0 bottom-0 w-1">
                    <!-- 外层光晕 -->
                    <div class="absolute inset-0 bg-gradient-to-b from-blue-500/30 via-cyan-500/20 to-transparent blur-sm"></div>
                    <!-- 主线条 -->
                    <div class="absolute inset-0 w-0.5 left-1/2 -translate-x-1/2 bg-gradient-to-b from-blue-500/80 via-cyan-500/60 to-blue-500/20"></div>
                    <!-- 内层高光 -->
                    <div class="absolute inset-0 w-px left-1/2 -translate-x-1/2 bg-gradient-to-b from-blue-300/60 via-cyan-300/40 to-transparent"></div>
                  </div>
                  
                  <div class="space-y-3 pl-12">
                    <div
                      v-for="(item, idx) in order.items.slice(1)"
                      :key="`${order.id}-accessory-${idx}`"
                      class="flex justify-between items-center group/accessory relative"
                    >
                      <!-- 连接节点 - 在竖线上的圆点 -->
                      <div class="absolute -left-[26px] top-1/2 -translate-y-1/2 z-10">
                        <div class="relative w-2.5 h-2.5">
                          <!-- 外圈光晕 -->
                          <div class="absolute inset-0 bg-blue-400/40 rounded-full blur-sm group-hover/accessory:bg-cyan-400/60 transition-all"></div>
                          <!-- 主圆点 -->
                          <div class="absolute inset-0 bg-gradient-to-br from-blue-400 to-cyan-500 rounded-full border border-blue-300/50 group-hover/accessory:scale-125 transition-transform"></div>
                          <!-- 内部高光 -->
                          <div class="absolute inset-[3px] bg-gradient-to-br from-white/60 to-transparent rounded-full"></div>
                        </div>
                      </div>
                      
                      <!-- 横向连接线 - 从节点到卡片 -->
                      <div class="absolute -left-[25px] top-1/2 -translate-y-1/2 w-7 h-1 z-0">
                        <!-- 外层光晕 -->
                        <div class="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-transparent blur-sm group-hover/accessory:from-cyan-500/50 transition-all"></div>
                        <!-- 主线条 -->
                        <div class="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500/70 via-cyan-500/50 to-transparent group-hover/accessory:from-blue-400 group-hover/accessory:via-cyan-400 transition-all"></div>
                        <!-- 顶部高光 -->
                        <div class="absolute top-1/2 -translate-y-1/2 left-0 right-0 h-px bg-gradient-to-r from-blue-300/50 via-cyan-300/30 to-transparent"></div>
                      </div>
                      
                      <!-- 配件内容卡片 -->
                      <div class="flex items-center gap-3 flex-1 bg-gradient-to-r from-slate-800/50 to-slate-800/30 hover:from-slate-800/70 hover:to-slate-800/50 rounded-xl p-2.5 transition-all border border-white/5 hover:border-cyan-500/40 shadow-lg hover:shadow-cyan-500/10 relative overflow-hidden group-hover/accessory:translate-x-1">
                        <!-- 卡片内部光效 -->
                        <div class="absolute inset-0 bg-gradient-to-r from-blue-500/0 via-cyan-500/5 to-blue-500/0 opacity-0 group-hover/accessory:opacity-100 transition-opacity"></div>
                        
                        <div class="w-10 h-10 bg-gradient-to-br from-gray-900 to-gray-800 rounded-lg border border-cyan-500/30 overflow-hidden group-hover/accessory:border-cyan-400/60 transition-all flex-shrink-0 shadow-inner relative">
                          <!-- 图片光晕 -->
                          <div class="absolute inset-0 bg-gradient-to-br from-cyan-500/10 to-transparent opacity-0 group-hover/accessory:opacity-100 transition-opacity"></div>
                          <img 
                            :src="getProductImage(item.itemName)" 
                            :alt="item.itemName"
                            class="w-full h-full object-cover group-hover/accessory:scale-110 transition-transform duration-300 relative z-10"
                            @error="(e) => e.target.src = '/images/shop/No1.png'"
                          />
                        </div>
                        
                        <div class="flex-1 min-w-0 relative z-10">
                          <p class="text-gray-300 text-sm font-semibold group-hover/accessory:text-cyan-300 transition-colors truncate">
                            {{ item.itemName }}
                          </p>
                          <p class="text-gray-600 text-xs mt-0.5 flex items-center gap-1.5">
                            <span class="text-gray-500">数量:</span>
                            <span class="text-gray-400 font-mono font-medium">{{ item.quantity }}</span>
                          </p>
                        </div>
                        
                        <div class="flex items-center gap-2.5 relative z-10">
                          <span class="px-2.5 py-1 bg-gradient-to-r from-emerald-500/15 to-emerald-600/10 border border-emerald-500/40 rounded-md text-emerald-400 font-bold text-[10px] shadow-sm">配件</span>
                          <span class="font-mono text-gray-200 font-semibold text-sm">¥{{ (item.price || 0).toFixed(2) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <!-- 兜底：如果没有items数据，显示原来的样式 -->
                <div v-if="!order.items || order.items.length === 0" class="flex items-start gap-4 group/item">
                  <div class="w-14 h-14 bg-slate-800 rounded-xl border border-white/5 flex items-center justify-center group-hover/item:bg-slate-700 transition-colors shrink-0">
                    <i class="ph-bold ph-package text-2xl text-slate-500 group-hover/item:text-blue-400 transition-colors"></i>
                  </div>
                  <div class="flex-1 flex items-start justify-between gap-4">
                    <div class="flex-1">
                      <p class="text-white font-medium group-hover/item:text-blue-300 transition-colors">{{ order.productName }}</p>
                      <div class="flex items-center gap-4 mt-2 text-xs text-slate-400">
                        <span>编码: <span class="text-slate-300 font-mono">{{ order.productCode }}</span></span>
                        <span>类型: <span class="text-slate-300">{{ order.productType === 'high-power' ? '大功率' : '小功率' }}</span></span>
                      </div>
                    </div>
                    <div class="text-right shrink-0">
                      <div class="text-blue-400 font-bold text-lg">¥{{ order.quantity > 0 ? (order.baseAmount / order.quantity).toFixed(2) : '0.00' }}</div>
                      <div class="text-slate-500 text-xs mt-0.5">× {{ order.quantity }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="w-px bg-white/5"></div>

            <!-- 中间：金额详情 -->
            <div class="w-64 space-y-3">
              <div class="relative rounded-xl p-4 border border-blue-500/20 bg-gradient-to-br from-blue-900/20 via-slate-900/50 to-purple-900/20">
                <div class="absolute top-0 right-0 w-20 h-20 bg-blue-500/10 rounded-full blur-2xl -z-10"></div>
                <div class="relative z-10">
                  <div class="flex items-center gap-1.5 mb-2">
                    <div class="w-1 h-1 rounded-full bg-blue-400 animate-pulse"></div>
                    <span class="text-slate-400 text-[10px] font-bold uppercase tracking-wider">订单总额</span>
                  </div>
                  <div class="flex items-baseline gap-1">
                    <span class="text-slate-400 text-lg font-bold">¥</span>
                    <div class="text-3xl font-black tracking-tight relative inline-block" style="font-family: 'Arial Black', sans-serif;">
                      <span class="absolute inset-0 text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 blur-sm">
                        {{ order.totalAmount }}
                      </span>
                      <span class="relative text-transparent bg-clip-text bg-gradient-to-r from-blue-300 via-cyan-200 to-purple-300">
                        {{ order.totalAmount }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 金额明细 -->
              <div class="space-y-2 text-xs">
                <div class="flex justify-between text-slate-400">
                  <span>商品金额</span>
                  <span class="font-mono text-slate-300">¥{{ order.baseAmount }}</span>
                </div>
                <div v-if="order.accessoriesAmount > 0" class="flex justify-between text-slate-400">
                  <span>配件金额</span>
                  <span class="font-mono text-slate-300">¥{{ order.accessoriesAmount }}</span>
                </div>
                <div v-if="order.shippingFee > 0" class="flex justify-between text-slate-400">
                  <span>运费</span>
                  <span class="font-mono text-slate-300">¥{{ order.shippingFee }}</span>
                </div>
                <div v-if="order.discountAmount > 0" class="flex justify-between text-green-400">
                  <span>优惠</span>
                  <span class="font-mono">-¥{{ order.discountAmount }}</span>
                </div>
              </div>

              <!-- 支付与物流信息 -->
              <div class="pt-3 border-t border-slate-700/50 space-y-2.5">
                <div class="flex justify-between items-center text-xs">
                  <span class="text-slate-400 flex items-center gap-1.5">
                    <i class="ph-bold ph-credit-card text-blue-400"></i>
                    支付方式
                  </span>
                  <span class="text-slate-300 font-medium">{{ getPaymentMethodName(order.paymentMethod) }}</span>
                </div>
                <div class="flex justify-between items-center text-xs">
                  <span class="text-slate-400 flex items-center gap-1.5">
                    <i class="ph-bold ph-truck text-purple-400"></i>
                    快递选择
                  </span>
                  <span class="text-slate-300 font-medium">{{ getCourierName(order.courierType) }}</span>
                </div>
              </div>
            </div>

            <div class="w-px bg-white/5"></div>

            <!-- 右侧：购买方信息 + 操作按钮 -->
            <div class="flex flex-col gap-3" style="min-width: 220px; max-width: 220px;">
              <!-- 购买方信息 -->
              <div v-if="order.buyerCompanyName || order.buyerRealName" class="p-3 bg-slate-900/50 rounded-lg border border-slate-700/30">
                <div class="flex items-center gap-2 mb-2">
                  <i class="ph-bold ph-buildings text-sm text-blue-400"></i>
                  <span class="text-[10px] text-slate-500 uppercase font-bold tracking-wider">购买方</span>
                </div>
                <div class="space-y-1.5">
                  <div v-if="order.buyerCompanyName" class="flex items-start gap-2">
                    <i class="ph-bold ph-building-office text-xs text-blue-400 mt-0.5 shrink-0"></i>
                    <p class="text-xs text-slate-300 leading-relaxed flex-1 break-words">{{ order.buyerCompanyName }}</p>
                  </div>
                  <div v-if="order.buyerRealName" class="flex items-start gap-2">
                    <i class="ph-bold ph-user text-xs text-green-400 mt-0.5 shrink-0"></i>
                    <p class="text-xs text-slate-300 leading-relaxed flex-1 break-words">{{ order.buyerRealName }}</p>
                  </div>
                </div>
              </div>

              <!-- 操作按钮 -->
              <div class="flex flex-col gap-2.5 flex-1 justify-end">
              <!-- 退款申请处理按钮 - 优先显示 -->
              <template v-if="order.refundStatus && (order.refundStatus === 'pending' || order.refundStatus === 'approved')">
                <button
                  @click="viewRefundDetail(order)"
                  class="w-full py-2.5 bg-gradient-to-r from-orange-600/20 to-yellow-600/20 text-orange-300 border border-orange-500/40 rounded-xl text-xs font-bold hover:from-orange-600/30 hover:to-yellow-600/30 flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-orange-900/20 animate-pulse"
                >
                  <i class="ph-bold ph-warning-circle"></i> 查看退款申请
                </button>
                <div class="flex gap-2">
                  <button
                    @click="approveRefund(order)"
                    class="flex-1 py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white border border-green-500/30 rounded-xl text-xs font-bold hover:from-green-700 hover:to-emerald-700 flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-green-900/30"
                  >
                    <i class="ph-bold ph-check-circle"></i> 同意
                  </button>
                  <button
                    @click="rejectRefund(order)"
                    class="flex-1 py-2.5 bg-red-600/20 text-red-300 border border-red-500/30 rounded-xl text-xs font-bold hover:bg-red-600/30 flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-red-900/20"
                  >
                    <i class="ph-bold ph-x-circle"></i> 驳回
                  </button>
                </div>
              </template>

              <!-- 常规操作按钮 -->
              <template v-if="order.status === 'pending'">
                <button
                  @click="cancelOrder(order.id)"
                  class="w-full py-2.5 bg-red-600/20 text-red-300 border border-red-500/30 rounded-xl text-xs font-bold hover:bg-red-600/30 flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-red-900/20"
                >
                  <i class="ph-bold ph-x-circle"></i> 取消订单
                </button>
              </template>

              <template v-else-if="order.status === 'paid'">
                <button
                  @click="openEditLogisticsModal(order)"
                  class="w-full py-2.5 bg-gradient-to-r from-green-600 to-emerald-600 text-white border border-green-500/30 rounded-xl text-xs font-bold hover:from-green-700 hover:to-emerald-700 flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-green-900/30"
                >
                  <i class="ph-bold ph-truck"></i> 标记发货
                </button>
                <button
                  v-if="!order.refundStatus || order.refundStatus === 'rejected'"
                  @click="refundOrder(order.id)"
                  class="w-full py-2.5 bg-white/5 text-slate-300 border border-white/10 rounded-xl text-xs font-medium hover:bg-white/10 hover:text-white hover:border-white/20 flex items-center justify-center gap-2 transition-all active:scale-95"
                >
                  <i class="ph-bold ph-arrow-counter-clockwise"></i> 退款
                </button>
              </template>

              <template v-else-if="order.status === 'shipped'">
                <button
                  @click="openEditLogisticsModal(order)"
                  class="w-full py-2.5 bg-purple-600/20 text-purple-300 border border-purple-500/30 rounded-xl text-xs font-bold hover:bg-purple-600/30 flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-purple-900/20"
                >
                  <i class="ph-bold ph-pencil-simple"></i> 修改物流
                </button>
                <button
                  @click="openAddTrackingModal(order)"
                  class="w-full py-2.5 bg-cyan-600/20 text-cyan-300 border border-cyan-500/30 rounded-xl text-xs font-medium hover:bg-cyan-600/30 flex items-center justify-center gap-2 transition-all active:scale-95"
                >
                  <i class="ph-bold ph-map-pin-line"></i> 更新物流轨迹
                </button>
              </template>

              <template v-else-if="order.status === 'delivered'">
                <button
                  @click="openAddTrackingModal(order)"
                  class="w-full py-2.5 bg-cyan-600/20 text-cyan-300 border border-cyan-500/30 rounded-xl text-xs font-medium hover:bg-cyan-600/30 flex items-center justify-center gap-2 transition-all active:scale-95"
                >
                  <i class="ph-bold ph-map-pin-line"></i> 更新物流轨迹
                </button>
                <button
                  v-if="!order.refundStatus || order.refundStatus === 'rejected'"
                  @click="refundOrder(order.id)"
                  class="w-full py-2.5 bg-white/5 text-slate-300 border border-white/10 rounded-xl text-xs font-medium hover:bg-white/10 hover:text-white hover:border-white/20 flex items-center justify-center gap-2 transition-all active:scale-95"
                >
                  <i class="ph-bold ph-arrow-counter-clockwise"></i> 退款
                </button>
              </template>

              <button
                @click="viewDetail(order)"
                class="w-full py-2.5 bg-blue-600/20 text-blue-300 border border-blue-500/30 rounded-xl text-xs font-medium hover:bg-blue-600/30 flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                <i class="ph-bold ph-eye"></i> 查看详情
              </button>
              </div>
            </div>
          </div>
        </div>
      </TransitionGroup>

      <!-- 分页组件 -->
      <div v-if="totalPages > 1" class="flex items-center justify-center gap-3 mt-6 pb-6">
        <!-- 上一页 -->
        <button
          @click="goToPage(currentPage - 1)"
          :disabled="currentPage === 1"
          class="group relative px-4 py-2.5 rounded-xl border transition-all duration-300"
          :class="currentPage === 1 
            ? 'bg-slate-800/50 border-slate-700/50 text-slate-600 cursor-not-allowed' 
            : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:bg-slate-700/50 hover:border-blue-500/50 hover:text-blue-300 active:scale-95'"
        >
          <div class="flex items-center gap-2">
            <i class="ph-bold ph-caret-left text-sm transition-transform group-hover:-translate-x-0.5"></i>
            <span class="text-sm font-medium">上一页</span>
          </div>
        </button>

        <!-- 页码 -->
        <div class="flex items-center gap-2">
          <template v-for="page in getPageNumbers()" :key="page">
            <button
              v-if="page !== '...'"
              @click="goToPage(page)"
              class="relative w-10 h-10 rounded-xl border font-bold text-sm transition-all duration-300"
              :class="currentPage === page
                ? 'bg-gradient-to-r from-blue-600/30 to-purple-600/30 border-blue-500/50 text-blue-300 shadow-[0_0_20px_rgba(59,130,246,0.3)] scale-110'
                : 'bg-slate-800/50 border-slate-700/50 text-slate-400 hover:bg-slate-700/50 hover:border-blue-500/30 hover:text-blue-300 active:scale-95'"
            >
              <span class="relative z-10">{{ page }}</span>
              <div v-if="currentPage === page" class="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-purple-600/20 rounded-xl blur animate-pulse"></div>
            </button>
            <span v-else class="text-slate-600 font-bold px-2">...</span>
          </template>
        </div>

        <!-- 下一页 -->
        <button
          @click="goToPage(currentPage + 1)"
          :disabled="currentPage === totalPages"
          class="group relative px-4 py-2.5 rounded-xl border transition-all duration-300"
          :class="currentPage === totalPages 
            ? 'bg-slate-800/50 border-slate-700/50 text-slate-600 cursor-not-allowed' 
            : 'bg-slate-800/50 border-slate-700/50 text-slate-300 hover:bg-slate-700/50 hover:border-blue-500/50 hover:text-blue-300 active:scale-95'"
        >
          <div class="flex items-center gap-2">
            <span class="text-sm font-medium">下一页</span>
            <i class="ph-bold ph-caret-right text-sm transition-transform group-hover:translate-x-0.5"></i>
          </div>
        </button>

        <!-- 快速跳转 -->
        <div class="flex items-center gap-2 ml-4 bg-slate-800/50 px-4 py-2.5 rounded-xl border border-slate-700/50 hover:border-blue-500/30 transition-all group">
          <span class="text-xs text-slate-500 group-hover:text-slate-400 transition-colors">跳转至</span>
          <input
            type="text"
            :value="currentPage"
            @input="(e) => e.target.value = e.target.value.replace(/[^0-9]/g, '')"
            @keyup.enter="(e) => goToPage(parseInt(e.target.value) || 1)"
            placeholder="页码"
            class="w-16 bg-slate-900/80 border border-slate-700/50 rounded-lg px-3 py-1.5 text-sm text-slate-200 text-center focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/30 transition-all placeholder-slate-600 font-mono"
            style="-moz-appearance: textfield;"
          />
          <span class="text-xs text-slate-500 group-hover:text-slate-400 transition-colors">页</span>
        </div>
      </div>
    </div>

    <!-- 详情弹窗 -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showDetailModal && selectedOrder" class="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" @click.self="showDetailModal = false">
          <div class="bg-slate-900 w-full max-w-2xl rounded-xl border border-slate-700 shadow-2xl max-h-[90vh] overflow-y-auto">
            <div class="p-5 border-b border-slate-700 flex justify-between items-center sticky top-0 bg-slate-900 z-10">
              <div>
                <h3 class="text-white text-lg font-bold">订单详情</h3>
                <p class="text-slate-500 text-sm font-mono mt-1">{{ selectedOrder.orderNumber }}</p>
              </div>
              <button @click="showDetailModal = false" class="text-slate-400 hover:text-white transition-colors">
                <i class="ph-bold ph-x text-2xl"></i>
              </button>
            </div>

            <div class="p-5 space-y-4">
              <!-- 基本信息 -->
              <div>
                <h4 class="text-sm font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
                  <i class="ph-bold ph-info"></i> 基本信息
                </h4>
                <div class="grid grid-cols-2 gap-3">
                  <div class="p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                    <span class="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
                      <i class="ph-bold ph-hash text-blue-400"></i>
                      订单ID
                    </span>
                    <p class="text-white font-mono">{{ selectedOrder.id }}</p>
                  </div>
                  <div class="p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                    <span class="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
                      <i class="ph-bold ph-user text-green-400"></i>
                      用户ID
                    </span>
                    <p class="text-white font-mono">{{ selectedOrder.userId }}</p>
                  </div>
                  <div class="p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                    <span class="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
                      <i class="ph-bold ph-flag text-purple-400"></i>
                      订单状态
                    </span>
                    <p class="text-white">
                      <span :class="statusMap[selectedOrder.status].color" class="px-2 py-1 rounded text-xs font-bold inline-flex items-center gap-1">
                        <i :class="statusMap[selectedOrder.status].icon"></i>
                        {{ statusMap[selectedOrder.status].label }}
                      </span>
                    </p>
                  </div>
                  <div class="p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                    <span class="text-xs text-slate-500 flex items-center gap-1.5 mb-1">
                      <i class="ph-bold ph-credit-card text-blue-400"></i>
                      支付方式
                    </span>
                    <p class="text-white mt-1 font-medium">{{ getPaymentMethodName(selectedOrder.paymentMethod) }}</p>
                  </div>
                </div>
              </div>

              <!-- 金额详情 -->
              <div>
                <h4 class="text-sm font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
                  <i class="ph-bold ph-currency-circle-dollar"></i> 金额详情
                </h4>
                <div class="p-4 bg-slate-800/50 rounded-lg border border-slate-700/30 space-y-2.5">
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-slate-400">商品金额</span>
                    <span class="text-white font-mono">¥{{ selectedOrder.baseAmount }}</span>
                  </div>
                  <div v-if="selectedOrder.accessoriesAmount > 0" class="flex justify-between items-center">
                    <span class="text-sm text-slate-400">配件金额</span>
                    <span class="text-white font-mono">¥{{ selectedOrder.accessoriesAmount }}</span>
                  </div>
                  <div class="flex justify-between items-center">
                    <span class="text-sm text-slate-400">运费</span>
                    <span class="text-white font-mono">¥{{ selectedOrder.shippingFee }}</span>
                  </div>
                  <div v-if="selectedOrder.discountAmount > 0" class="flex justify-between items-center text-green-400">
                    <span class="text-sm">优惠金额</span>
                    <span class="font-mono">-¥{{ selectedOrder.discountAmount }}</span>
                  </div>
                  <div v-if="selectedOrder.couponCode" class="flex justify-between items-center text-xs text-slate-500">
                    <span>优惠券</span>
                    <span class="font-mono">{{ selectedOrder.couponCode }}</span>
                  </div>
                  <div class="pt-2.5 mt-2.5 border-t border-slate-700/50 flex justify-between items-center">
                    <span class="text-base font-bold text-white">订单总额</span>
                    <span class="text-2xl font-black text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400">
                      ¥{{ selectedOrder.totalAmount }}
                    </span>
                  </div>
                  
                  <!-- 支付与物流信息 -->
                  <div class="pt-3 mt-3 border-t border-slate-700/50 space-y-2.5">
                    <div class="flex justify-between items-center text-sm">
                      <span class="text-slate-400 flex items-center gap-2">
                        <i class="ph-bold ph-credit-card text-blue-400"></i>
                        支付方式
                      </span>
                      <span class="text-white font-medium">{{ getPaymentMethodName(selectedOrder.paymentMethod) }}</span>
                    </div>
                    <div class="flex justify-between items-center text-sm">
                      <span class="text-slate-400 flex items-center gap-2">
                        <i class="ph-bold ph-truck text-purple-400"></i>
                        快递选择
                      </span>
                      <span class="text-white font-medium">{{ getCourierName(selectedOrder.courierType) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 时间信息 -->
              <div>
                <h4 class="text-sm font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
                  <i class="ph-bold ph-clock"></i> 时间信息
                </h4>
                <div class="grid grid-cols-2 gap-3">
                  <div class="p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                    <div class="flex items-center gap-2 mb-2">
                      <i class="ph-bold ph-calendar-plus text-blue-400 text-sm"></i>
                      <span class="text-xs text-slate-500">创建时间</span>
                    </div>
                    <p class="text-white font-mono text-sm tracking-wide">{{ formatDateTime(selectedOrder.createdAt) }}</p>
                  </div>
                  <div class="p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                    <div class="flex items-center gap-2 mb-2">
                      <i class="ph-bold ph-credit-card text-green-400 text-sm"></i>
                      <span class="text-xs text-slate-500">支付时间</span>
                    </div>
                    <p class="text-white font-mono text-sm tracking-wide">{{ selectedOrder.paymentTime ? formatDateTime(selectedOrder.paymentTime) : '-' }}</p>
                  </div>
                  <div class="p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                    <div class="flex items-center gap-2 mb-2">
                      <i class="ph-bold ph-truck text-purple-400 text-sm"></i>
                      <span class="text-xs text-slate-500">发货时间</span>
                    </div>
                    <p class="text-white font-mono text-sm tracking-wide">{{ selectedOrder.shippingTime ? formatDateTime(selectedOrder.shippingTime) : '-' }}</p>
                  </div>
                  <div class="p-3 bg-slate-800/50 rounded-lg border border-slate-700/30">
                    <div class="flex items-center gap-2 mb-2">
                      <i class="ph-bold ph-check-circle text-emerald-400 text-sm"></i>
                      <span class="text-xs text-slate-500">签收时间</span>
                    </div>
                    <p class="text-white font-mono text-sm tracking-wide">{{ selectedOrder.deliveryTime ? formatDateTime(selectedOrder.deliveryTime) : '-' }}</p>
                  </div>
                </div>
              </div>

              <!-- 商品信息 -->
              <div>
                <h4 class="text-sm font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
                  <i class="ph-bold ph-package"></i> 商品信息
                </h4>
                <div class="p-4 bg-slate-800/50 rounded-lg border border-slate-700/30">
                  <div class="flex items-start gap-3 mb-3">
                    <div class="w-12 h-12 bg-slate-700/50 rounded-lg flex items-center justify-center shrink-0">
                      <i class="ph-bold ph-cpu text-2xl text-blue-400"></i>
                    </div>
                    <div class="flex-1">
                      <p class="text-white font-medium">{{ selectedOrder.productName }}</p>
                      <p class="text-xs text-slate-500 mt-1">{{ selectedOrder.productType === 'high-power' ? '大功率设备' : '小功率设备' }}</p>
                    </div>
                  </div>
                  <div class="grid grid-cols-3 gap-4 pt-3 border-t border-slate-700/50 text-sm">
                    <div>
                      <span class="text-slate-500 text-xs">商品编码</span>
                      <p class="text-white font-mono mt-1 text-xs">{{ selectedOrder.productCode }}</p>
                    </div>
                    <div>
                      <span class="text-slate-500 text-xs">数量</span>
                      <p class="text-white font-mono mt-1">{{ selectedOrder.quantity }}</p>
                    </div>
                    <div>
                      <span class="text-slate-500 text-xs">快递</span>
                      <p class="text-white mt-1 text-xs">{{ getCourierName(selectedOrder.courierType) }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 购买方信息 -->
              <div v-if="selectedOrder.buyerCompanyName || selectedOrder.buyerRealName">
                <h4 class="text-sm font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
                  <i class="ph-bold ph-buildings"></i> 购买方信息
                </h4>
                <div class="p-4 bg-slate-800/50 rounded-lg border border-slate-700/30 space-y-2">
                  <div v-if="selectedOrder.buyerCompanyName" class="flex items-start gap-3">
                    <i class="ph-bold ph-building-office text-blue-400 mt-0.5"></i>
                    <div>
                      <span class="text-xs text-slate-500">公司名称</span>
                      <p class="text-white mt-0.5">{{ selectedOrder.buyerCompanyName }}</p>
                    </div>
                  </div>
                  <div v-if="selectedOrder.buyerRealName" class="flex items-start gap-3">
                    <i class="ph-bold ph-user text-green-400 mt-0.5"></i>
                    <div>
                      <span class="text-xs text-slate-500">联系人</span>
                      <p class="text-white mt-0.5">{{ selectedOrder.buyerRealName }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 收货地址 -->
              <div v-if="selectedOrder.shippingAddress">
                <h4 class="text-sm font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
                  <i class="ph-bold ph-map-pin"></i> 收货地址
                </h4>
                <div class="p-4 bg-slate-800/50 rounded-lg border border-slate-700/30 space-y-3">
                  <!-- 收货人信息 -->
                  <div class="flex items-center gap-4 pb-3 border-b border-slate-700/50">
                    <div class="flex items-center gap-2 flex-1">
                      <i class="ph-bold ph-user text-blue-400"></i>
                      <span class="text-xs text-slate-500">收货人</span>
                      <span class="text-white font-medium">{{ selectedOrder.shippingAddress.name || '-' }}</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <i class="ph-bold ph-phone text-green-400"></i>
                      <span class="text-xs text-slate-500">电话</span>
                      <span class="text-white font-mono">{{ selectedOrder.shippingAddress.phone || '-' }}</span>
                    </div>
                  </div>
                  
                  <!-- 地址信息 -->
                  <div class="space-y-2.5">
                    <!-- 地区 + 邮编（同一行） -->
                    <div class="flex items-start gap-4">
                      <!-- 地区 -->
                      <div class="flex-1">
                        <div class="flex items-center gap-2">
                          <i class="ph-bold ph-map-trifold text-purple-400"></i>
                          <span class="text-xs text-slate-500">地区</span>
                        </div>
                        <div class="flex items-center gap-2 mt-1">
                          <i class="ph-bold ph-map-trifold text-transparent"></i>
                          <p class="text-white text-sm">
                            {{ selectedOrder.shippingAddress.province || '' }}
                            {{ selectedOrder.shippingAddress.city || '' }}
                            {{ selectedOrder.shippingAddress.district || '' }}
                          </p>
                        </div>
                      </div>
                      
                      <!-- 邮编 -->
                      <div v-if="selectedOrder.shippingAddress.postal_code || selectedOrder.shippingAddress.postalCode" style="min-width: 160px;">
                        <div class="flex items-center gap-2">
                          <i class="ph-bold ph-envelope text-cyan-400"></i>
                          <span class="text-xs text-slate-500">邮编</span>
                        </div>
                        <div class="flex items-center gap-2 mt-1">
                          <i class="ph-bold ph-envelope text-transparent"></i>
                          <p class="text-white font-mono text-sm">{{ selectedOrder.shippingAddress.postal_code || selectedOrder.shippingAddress.postalCode }}</p>
                        </div>
                      </div>
                    </div>
                    
                    <!-- 详细地址 -->
                    <div>
                      <div class="flex items-center gap-2">
                        <i class="ph-bold ph-house text-orange-400"></i>
                        <span class="text-xs text-slate-500">详细地址</span>
                      </div>
                      <div class="flex items-start gap-2 mt-1">
                        <i class="ph-bold ph-house text-transparent"></i>
                        <p class="text-white text-sm leading-relaxed">{{ selectedOrder.shippingAddress.address || selectedOrder.shippingAddress.detail || '-' }}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 物流追踪 -->
              <div v-if="selectedOrder.logistics && (selectedOrder.status === 'shipped' || selectedOrder.status === 'delivered')">
                <h4 class="text-sm font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
                  <i class="ph-bold ph-navigation"></i> 物流追踪
                </h4>
                <div class="p-5 bg-slate-800/50 rounded-lg border border-slate-700/30">
                  <!-- 物流时间线 -->
                  <div class="space-y-0 relative pl-2">
                    <div class="absolute left-[11px] top-2 bottom-4 w-0.5 bg-slate-700"></div>
                    
                    <!-- 发货地 -->
                    <div v-if="selectedOrder.logistics.originLocation" class="relative pl-6 pb-4 group/step">
                      <div class="absolute left-0 top-1 w-2.5 h-2.5 rounded-full border-2 z-10 transition-all duration-300 bg-blue-500 border-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]"></div>
                      <p class="text-xs font-medium text-blue-300">已发货</p>
                      <p class="text-[10px] text-slate-500 font-mono mt-0.5 tracking-tight">发货地：{{ selectedOrder.logistics.originLocation }}</p>
                    </div>
                    
                    <!-- 中转地列表 -->
                    <div 
                      v-for="(transit, idx) in selectedOrder.logistics.transitLocations || []" 
                      :key="`transit-${idx}`"
                      class="relative pl-6 pb-4 group/step"
                    >
                      <div class="absolute left-0 top-1 w-2.5 h-2.5 rounded-full border-2 z-10 transition-all duration-300 bg-purple-500 border-purple-500 shadow-[0_0_8px_rgba(168,85,247,0.8)]"></div>
                      <p class="text-xs font-medium text-purple-300">运输中</p>
                      <p class="text-[10px] text-slate-500 font-mono mt-0.5 tracking-tight">中转地：{{ transit }}</p>
                    </div>
                    
                    <!-- 送达地 -->
                    <div v-if="selectedOrder.logistics.destinationLocation || selectedOrder.shippingAddress" class="relative pl-6 pb-0 group/step">
                      <div 
                        class="absolute left-0 top-1 w-2.5 h-2.5 rounded-full border-2 z-10 transition-all duration-300"
                        :class="selectedOrder.status === 'delivered' 
                          ? 'bg-green-500 border-green-500 shadow-[0_0_8px_rgba(34,197,94,0.8)]' 
                          : 'bg-slate-900 border-slate-600'"
                      ></div>
                      <p 
                        class="text-xs font-medium"
                        :class="selectedOrder.status === 'delivered' ? 'text-green-300' : 'text-slate-500'"
                      >
                        {{ selectedOrder.status === 'delivered' ? '已送达' : '待送达' }}
                      </p>
                      <p class="text-[10px] text-slate-500 font-mono mt-0.5 tracking-tight">
                        送达地：{{ selectedOrder.logistics.destinationLocation || getDestinationFromAddress(selectedOrder.shippingAddress) }}
                      </p>
                    </div>
                  </div>
                  
                  <!-- 快递信息 -->
                  <div v-if="selectedOrder.logistics.trackingNumber" class="mt-4 pt-4 border-t border-slate-700/50 flex items-center justify-between text-xs">
                    <div class="flex items-center gap-2 text-slate-400">
                      <i class="ph-bold ph-barcode text-cyan-400"></i>
                      <span>快递单号</span>
                    </div>
                    <span class="text-white font-mono">{{ selectedOrder.logistics.trackingNumber }}</span>
                  </div>
                </div>
              </div>

              <!-- 备注 -->
              <div v-if="selectedOrder.remark">
                <h4 class="text-sm font-bold text-slate-400 uppercase mb-3 flex items-center gap-2">
                  <i class="ph-bold ph-note"></i> 订单备注
                </h4>
                <div class="p-4 bg-slate-800/50 rounded-lg border border-slate-700/30">
                  <p class="text-slate-300 text-sm whitespace-pre-wrap leading-relaxed">{{ selectedOrder.remark }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 修改物流弹窗 -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showEditLogisticsModal && editingOrder" class="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" @click.self="closeEditLogisticsModal">
          <div class="bg-slate-900 w-full max-w-3xl rounded-2xl border border-slate-700 shadow-2xl max-h-[90vh] overflow-y-auto">
            <!-- 弹窗头部 -->
            <div class="p-6 border-b border-slate-700 flex justify-between items-center sticky top-0 bg-slate-900 z-10">
              <div>
                <h3 class="text-xl font-bold text-white flex items-center gap-3">
                  <i class="ph-bold ph-pencil-simple text-purple-400"></i>
                  修改物流信息
                </h3>
                <p class="text-slate-500 text-sm font-mono mt-1">{{ editingOrder.orderNumber }}</p>
              </div>
              <button @click="closeEditLogisticsModal" class="text-slate-400 hover:text-white transition-colors">
                <i class="ph-bold ph-x text-2xl"></i>
              </button>
            </div>

            <!-- 弹窗内容 -->
            <div class="p-6 space-y-6">
              <!-- 订单状态 -->
              <div class="space-y-3">
                <label class="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <i class="ph-bold ph-flag text-blue-400"></i>
                  订单状态
                  <span class="text-xs text-slate-500 font-normal ml-2">（当前：{{ getStatusLabel(editingOrder.status) }}）</span>
                </label>
                <div class="grid grid-cols-3 gap-3">
                  <button
                    v-for="status in allStatuses"
                    :key="status.id"
                    @click="!isStatusDisabled(status.id) && (logisticsForm.status = status.id)"
                    :disabled="isStatusDisabled(status.id)"
                    :class="[
                      'py-3 px-4 rounded-xl text-sm font-bold border-2 transition-all flex items-center justify-center gap-2',
                      logisticsForm.status === status.id
                        ? 'bg-purple-600/30 border-purple-500 text-purple-300 shadow-lg shadow-purple-900/30'
                        : isStatusDisabled(status.id)
                        ? 'bg-slate-900/50 border-slate-800 text-slate-600 cursor-not-allowed opacity-40'
                        : 'bg-slate-800/50 border-slate-700 text-slate-400 hover:border-slate-600 cursor-pointer'
                    ]"
                  >
                    <i :class="status.icon"></i>
                    {{ status.label }}
                  </button>
                </div>
                <p class="text-xs text-slate-500 mt-2">
                  <i class="ph-bold ph-info"></i>
                  可以选择当前状态或下一步状态，之前的状态不可选
                </p>
              </div>

              <!-- 动态字段区域 - 带过渡动画 -->
              <TransitionGroup name="field-fade" tag="div" class="space-y-6">
                <!-- 快递公司修改 - 仅在已发货/已签收状态显示 -->
                <div v-if="shouldShowLogisticsFields" key="courier" class="space-y-3">
                <label class="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <i class="ph-bold ph-truck text-green-400"></i>
                  快递公司修改
                  <span v-if="logisticsForm.status === 'delivered'" class="text-xs text-slate-500 font-normal">（已签收不可修改）</span>
                </label>
                <div class="relative">
                  <button
                    type="button"
                    @click="logisticsForm.status !== 'delivered' && (courierDropdownOpen = !courierDropdownOpen)"
                    :disabled="logisticsForm.status === 'delivered'"
                    :class="[
                      'w-full border rounded-xl px-4 py-3 outline-none transition-all flex items-center justify-between',
                      logisticsForm.status === 'delivered'
                        ? 'bg-slate-900/50 border-slate-800 text-slate-600 cursor-not-allowed'
                        : 'bg-slate-800/50 border-slate-700 text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 hover:bg-slate-800/70'
                    ]"
                  >
                    <div v-if="logisticsForm.courierType" class="flex items-center gap-3">
                      <div class="w-10 h-10 flex items-center justify-center shrink-0">
                        <img 
                          :src="getCourierLogo(logisticsForm.courierType)" 
                          :alt="getCourierName(logisticsForm.courierType)"
                          class="w-8 h-8 object-contain rounded-lg"
                          @error="handleImageError"
                        />
                      </div>
                      <div class="text-left">
                        <div class="text-sm font-bold">{{ getCourierName(logisticsForm.courierType) }}</div>
                        <div class="text-xs text-slate-400">{{ getCourierEn(logisticsForm.courierType) || '快递服务' }}</div>
                      </div>
                    </div>
                    <span v-else class="text-slate-500">请选择快递公司</span>
                    <i :class="['ph-bold text-slate-400 transition-transform', courierDropdownOpen ? 'ph-caret-up' : 'ph-caret-down']"></i>
                  </button>
                  
                  <Transition name="dropdown">
                    <div
                      v-if="courierDropdownOpen"
                      class="absolute z-[70] mt-2 w-full bg-slate-800/95 border border-purple-500/30 rounded-xl shadow-2xl backdrop-blur-lg overflow-hidden"
                    >
                      <div class="max-h-80 overflow-y-auto custom-scrollbar">
                        <button
                          v-for="courier in courierDisplayOptions"
                          :key="courier.id"
                          type="button"
                          @click="selectCourier(courier.id)"
                          class="w-full px-4 py-3 flex items-center gap-3 hover:bg-purple-600/20 transition-all border-b border-slate-700/50 last:border-b-0 relative"
                          :class="logisticsForm.courierType === courier.id || getCourierName(logisticsForm.courierType) === courier.name ? 'bg-purple-600/10' : ''"
                        >
                          <div class="w-10 h-10 flex items-center justify-center shrink-0">
                            <img 
                              :src="courier.logo" 
                              :alt="courier.name"
                              class="w-8 h-8 object-contain rounded-lg"
                            />
                          </div>
                          <div class="flex-1 text-left">
                            <div class="text-sm font-bold text-white">{{ courier.name }}</div>
                            <div class="text-xs text-slate-400">{{ courier.en }}</div>
                          </div>
                          <div v-if="courier.eta" class="text-xs text-green-400 font-medium px-2 py-1 bg-green-500/10 rounded-md border border-green-500/20">
                            {{ courier.eta }}
                          </div>
                          <i v-if="logisticsForm.courierType === courier.id || getCourierName(logisticsForm.courierType) === courier.name" class="ph-bold ph-check text-purple-400 absolute top-2 right-2"></i>
                        </button>
                      </div>
                    </div>
                  </Transition>
                </div>
              </div>

              <!-- 快递单号 - 仅在已发货/已签收状态显示 -->
              <div v-if="shouldShowLogisticsFields" key="tracking" class="space-y-3">
                <label class="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <i class="ph-bold ph-barcode text-cyan-400"></i>
                  快递单号
                  <span v-if="logisticsForm.status === 'delivered'" class="text-xs text-slate-500 font-normal">（已签收不可修改）</span>
                </label>
                <input
                  v-model="logisticsForm.trackingNumber"
                  type="text"
                  placeholder="请输入快递单号"
                  :disabled="logisticsForm.status === 'delivered'"
                  :class="[
                    'w-full border rounded-xl px-4 py-3 placeholder-slate-500 outline-none transition-all font-mono',
                    logisticsForm.status === 'delivered'
                      ? 'bg-slate-900/50 border-slate-800 text-slate-600 cursor-not-allowed'
                      : 'bg-slate-800/50 border-slate-700 text-white focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20'
                  ]"
                />
              </div>

              <!-- 当前位置 - 仅在已发货/已签收状态显示 -->
              <div v-if="shouldShowLogisticsFields" key="location" class="space-y-3">
                <label class="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <i class="ph-bold ph-map-pin text-orange-400"></i>
                  当前位置
                </label>
                <input
                  v-model="logisticsForm.currentLocation"
                  type="text"
                  placeholder="例如：北京市朝阳区分拨中心"
                  class="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition-all"
                />
              </div>

              <!-- 物流状态描述 - 仅在已发货/已签收状态显示 -->
              <div v-if="shouldShowLogisticsFields" key="description" class="space-y-3">
                <label class="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <i class="ph-bold ph-note text-yellow-400"></i>
                  物流状态描述
                </label>
                <textarea
                  v-model="logisticsForm.statusDescription"
                  rows="3"
                  placeholder="例如：快件已到达北京市朝阳区分拨中心，正在进行分拣"
                  class="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition-all resize-none"
                ></textarea>
              </div>

              <!-- 预计送达时间 - 仅在已发货状态显示 -->
              <div v-if="logisticsForm.status === 'shipped'" key="delivery-time" class="space-y-3">
                <label class="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <i class="ph-bold ph-calendar text-pink-400"></i>
                  预计送达时间
                </label>
                <div class="relative">
                  <div class="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-2 z-[5]" :class="showDateTimePicker ? 'pointer-events-none' : 'pointer-events-none'">
                    <i class="ph-bold ph-clock text-pink-400 text-lg drop-shadow-[0_0_8px_rgba(236,72,153,0.6)]"></i>
                  </div>
                  <button
                    type="button"
                    @click="showDateTimePicker = !showDateTimePicker"
                    class="w-full bg-slate-800/50 border border-slate-700 rounded-xl pl-12 pr-12 py-3 text-left hover:bg-slate-800/70 focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 outline-none transition-all relative z-[6]"
                    :class="logisticsForm.estimatedDelivery ? 'border-pink-500/30 bg-gradient-to-r from-pink-900/10 via-slate-800/50 to-slate-800/50' : ''"
                    style="background-image: linear-gradient(to right, rgba(236, 72, 153, 0.05), transparent);"
                  >
                    <span v-if="logisticsForm.estimatedDelivery" class="text-transparent bg-clip-text bg-gradient-to-r from-pink-200 via-white to-pink-200 font-bold text-base" style="font-family: 'SF Pro Display', 'PingFang SC', -apple-system, BlinkMacSystemFont, sans-serif; letter-spacing: 0.5px;">
                      {{ formatDateTimeDisplay(logisticsForm.estimatedDelivery) }}
                    </span>
                    <span v-else class="text-slate-300 font-medium">选择预计送达时间</span>
                  </button>
                  <div class="absolute right-4 top-1/2 -translate-y-1/2 z-[5]" :class="showDateTimePicker ? 'pointer-events-none' : 'pointer-events-none'">
                    <i :class="['ph-bold transition-transform', showDateTimePicker ? 'ph-caret-down text-pink-400' : 'ph-caret-up text-slate-600']"></i>
                  </div>
                  
                  <!-- 自定义日期时间选择器 - 向上展开 -->
                  <Transition name="dropdown-up">
                    <div
                      v-if="showDateTimePicker"
                      class="absolute z-[80] bottom-full mb-2 right-0 w-auto min-w-[400px] bg-slate-800/95 border border-pink-500/30 rounded-xl shadow-2xl backdrop-blur-lg overflow-hidden"
                    >
                      <div class="p-5">
                        <!-- 日期选择 -->
                        <div class="mb-4">
                          <div class="text-xs text-slate-400 mb-2.5 font-bold uppercase tracking-wider">日期</div>
                          <div class="flex items-center justify-center gap-2">
                            <input
                              v-model="tempDateTime.year"
                              type="text"
                              placeholder="2026"
                              maxlength="4"
                              @input="validateNumberInput($event, 'year', 4)"
                              :key="`year-${tempDateTime.year}`"
                              class="w-24 bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-lg font-bold text-center focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 outline-none transition-all number-change"
                            />
                            <span class="text-slate-500 text-sm">年</span>
                            <input
                              v-model="tempDateTime.month"
                              type="text"
                              placeholder="01"
                              maxlength="2"
                              @input="validateNumberInput($event, 'month', 2)"
                              :key="`month-${tempDateTime.month}`"
                              class="w-16 bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-lg font-bold text-center focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 outline-none transition-all number-change"
                            />
                            <span class="text-slate-500 text-sm">月</span>
                            <input
                              v-model="tempDateTime.day"
                              type="text"
                              placeholder="23"
                              maxlength="2"
                              @input="validateNumberInput($event, 'day', 2)"
                              :key="`day-${tempDateTime.day}`"
                              class="w-16 bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-lg font-bold text-center focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 outline-none transition-all number-change"
                            />
                            <span class="text-slate-500 text-sm">日</span>
                          </div>
                        </div>
                        
                        <!-- 时间选择 -->
                        <div class="mb-4">
                          <div class="text-xs text-slate-400 mb-2.5 font-bold uppercase tracking-wider">时间</div>
                          <div class="flex items-center justify-center gap-3">
                            <input
                              v-model="tempDateTime.hour"
                              type="text"
                              placeholder="12"
                              maxlength="2"
                              @input="validateNumberInput($event, 'hour', 2)"
                              :key="`hour-${tempDateTime.hour}`"
                              class="w-20 bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-lg font-bold text-center focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 outline-none transition-all number-change"
                            />
                            <span class="text-slate-400 text-2xl font-bold">:</span>
                            <input
                              v-model="tempDateTime.minute"
                              type="text"
                              placeholder="00"
                              maxlength="2"
                              @input="validateNumberInput($event, 'minute', 2)"
                              :key="`minute-${tempDateTime.minute}`"
                              class="w-20 bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2.5 text-white text-lg font-bold text-center focus:border-pink-500 focus:ring-2 focus:ring-pink-500/20 outline-none transition-all number-change"
                            />
                          </div>
                        </div>
                        
                        <!-- 快捷选择 -->
                        <div class="mb-4">
                          <div class="text-xs text-slate-400 mb-2.5 font-bold uppercase tracking-wider">快捷选择</div>
                          <div class="flex gap-2 justify-center">
                            <button
                              type="button"
                              @click="setQuickDateTime(1)"
                              class="px-4 py-2 bg-slate-900/50 hover:bg-pink-600/20 border border-slate-700 hover:border-pink-500/50 rounded-lg text-xs text-slate-300 hover:text-pink-300 transition-all font-medium"
                            >
                              明天
                            </button>
                            <button
                              type="button"
                              @click="setQuickDateTime(3)"
                              class="px-4 py-2 bg-slate-900/50 hover:bg-pink-600/20 border border-slate-700 hover:border-pink-500/50 rounded-lg text-xs text-slate-300 hover:text-pink-300 transition-all font-medium"
                            >
                              3天后
                            </button>
                            <button
                              type="button"
                              @click="setQuickDateTime(7)"
                              class="px-4 py-2 bg-slate-900/50 hover:bg-pink-600/20 border border-slate-700 hover:border-pink-500/50 rounded-lg text-xs text-slate-300 hover:text-pink-300 transition-all font-medium"
                            >
                              7天后
                            </button>
                          </div>
                        </div>
                        
                        <!-- 操作按钮 -->
                        <div class="flex gap-3 pt-3 border-t border-slate-700/50">
                          <button
                            type="button"
                            @click="clearDateTime"
                            class="flex-1 px-4 py-2.5 bg-slate-900/50 hover:bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-400 hover:text-white transition-all font-medium"
                          >
                            清除
                          </button>
                          <button
                            type="button"
                            @click="confirmDateTime"
                            class="flex-1 px-4 py-2.5 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-500 hover:to-purple-500 rounded-lg text-sm text-white font-bold transition-all shadow-lg shadow-pink-500/25"
                          >
                            确定
                          </button>
                        </div>
                      </div>
                    </div>
                  </Transition>
                </div>
              </div>
              </TransitionGroup>

              <!-- 备注 - 始终显示 -->
              <div class="space-y-3">
                <label class="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <i class="ph-bold ph-chat-text text-indigo-400"></i>
                  备注信息
                </label>
                <textarea
                  v-model="logisticsForm.remark"
                  rows="2"
                  placeholder="其他备注信息（可选）"
                  class="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 outline-none transition-all resize-none"
                ></textarea>
              </div>
            </div>

            <!-- 弹窗底部按钮 -->
            <div class="p-6 border-t border-slate-700 flex gap-3 sticky bottom-0 bg-slate-900 z-[10]">
              <button
                @click="closeEditLogisticsModal"
                class="flex-1 py-3 bg-slate-800 text-slate-300 rounded-xl font-bold hover:bg-slate-700 transition-all"
              >
                取消
              </button>
              <button
                @click="saveLogistics"
                :disabled="savingLogistics"
                class="flex-1 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-bold hover:from-purple-500 hover:to-blue-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                <i v-if="!savingLogistics" class="ph-bold ph-check"></i>
                <i v-else class="ph-bold ph-circle-notch animate-spin"></i>
                {{ savingLogistics ? '保存中...' : '保存修改' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 更新物流轨迹弹窗 -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showAddTrackingModal && editingOrder" class="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" @click.self="closeAddTrackingModal">
          <div class="bg-slate-900 w-full max-w-2xl rounded-2xl border border-slate-700 shadow-2xl">
            <!-- 弹窗头部 -->
            <div class="p-6 border-b border-slate-700 flex justify-between items-center bg-gradient-to-r from-cyan-900/20 to-slate-900">
              <div>
                <h3 class="text-xl font-bold text-white flex items-center gap-3">
                  <i class="ph-bold ph-map-pin-line text-cyan-400"></i>
                  更新物流轨迹
                </h3>
                <p class="text-slate-500 text-sm font-mono mt-1">{{ editingOrder.orderNumber }}</p>
              </div>
              <button @click="closeAddTrackingModal" class="text-slate-400 hover:text-white transition-colors">
                <i class="ph-bold ph-x text-2xl"></i>
              </button>
            </div>

            <!-- 弹窗内容 -->
            <div class="p-6 space-y-5">
              <!-- 状态描述 -->
              <div class="space-y-2">
                <label class="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <i class="ph-bold ph-info text-cyan-400"></i>
                  状态描述
                  <span class="text-red-400">*</span>
                </label>
                <input
                  v-model="trackingForm.status"
                  type="text"
                  placeholder="例如：快件已到达【北京分拨中心】"
                  class="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 transition-all placeholder-slate-500"
                />
              </div>

              <!-- 位置 -->
              <div class="space-y-2">
                <label class="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <i class="ph-bold ph-map-pin text-green-400"></i>
                  当前位置
                </label>
                <input
                  v-model="trackingForm.location"
                  type="text"
                  placeholder="例如：北京市朝阳区"
                  class="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-500/20 transition-all placeholder-slate-500"
                />
              </div>

              <!-- 跟踪时间 -->
              <div class="space-y-2">
                <label class="text-sm font-bold text-slate-300 uppercase tracking-wider flex items-center gap-2">
                  <i class="ph-bold ph-clock text-purple-400"></i>
                  跟踪时间
                  <span class="text-red-400">*</span>
                </label>
                <div class="relative">
                  <button
                    type="button"
                    @click="showTrackingTimePicker = !showTrackingTimePicker"
                    class="w-full bg-slate-800/50 border border-slate-700 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all flex items-center justify-between hover:bg-slate-800/70"
                  >
                    <span v-if="trackingForm.trackingTime" class="font-mono">{{ formatDisplayDateTime(trackingForm.trackingTime) }}</span>
                    <span v-else class="text-slate-500">选择日期和时间</span>
                    <i class="ph-bold ph-calendar-blank text-purple-400"></i>
                  </button>
                  
                  <!-- 自定义日期时间选择器 -->
                  <Transition name="dropdown">
                    <div
                      v-if="showTrackingTimePicker"
                      class="absolute z-[80] mt-2 bg-slate-800/95 border border-purple-500/30 rounded-xl shadow-2xl backdrop-blur-lg overflow-hidden"
                      style="left: 50%; transform: translateX(-50%);"
                    >
                      <div class="p-4 space-y-4 w-80">
                        <!-- 日期选择 -->
                        <div>
                          <div class="text-xs font-bold text-slate-400 uppercase mb-2">日期</div>
                          <div class="grid grid-cols-3 gap-2">
                            <div>
                              <input
                                v-model.number="trackingDateTime.year"
                                type="number"
                                placeholder="年"
                                @input="validateNumberInput($event, 'year', 4)"
                                @keypress="onlyNumbers"
                                class="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                              />
                            </div>
                            <div>
                              <input
                                v-model.number="trackingDateTime.month"
                                type="number"
                                min="1"
                                max="12"
                                placeholder="月"
                                @input="validateNumberInput($event, 'month', 2)"
                                @keypress="onlyNumbers"
                                class="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                              />
                            </div>
                            <div>
                              <input
                                v-model.number="trackingDateTime.day"
                                type="number"
                                min="1"
                                max="31"
                                placeholder="日"
                                @input="validateNumberInput($event, 'day', 2)"
                                @keypress="onlyNumbers"
                                class="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <!-- 时间选择 -->
                        <div>
                          <div class="text-xs font-bold text-slate-400 uppercase mb-2">时间</div>
                          <div class="grid grid-cols-2 gap-2">
                            <div>
                              <input
                                v-model.number="trackingDateTime.hour"
                                type="number"
                                min="0"
                                max="23"
                                placeholder="时"
                                @input="validateNumberInput($event, 'hour', 2)"
                                @keypress="onlyNumbers"
                                class="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                              />
                            </div>
                            <div>
                              <input
                                v-model.number="trackingDateTime.minute"
                                type="number"
                                min="0"
                                max="59"
                                placeholder="分"
                                @input="validateNumberInput($event, 'minute', 2)"
                                @keypress="onlyNumbers"
                                class="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-purple-500"
                              />
                            </div>
                          </div>
                        </div>
                        
                        <!-- 快捷按钮 -->
                        <div class="flex gap-2">
                          <button
                            type="button"
                            @click="setTrackingTimeToNow"
                            class="flex-1 py-2 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-lg text-xs font-medium transition-all"
                          >
                            现在
                          </button>
                          <button
                            type="button"
                            @click="confirmTrackingTime"
                            class="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-xs font-bold transition-all"
                          >
                            确定
                          </button>
                        </div>
                      </div>
                    </div>
                  </Transition>
                </div>
              </div>

              <!-- 提示信息 -->
              <div class="p-4 bg-cyan-900/10 border border-cyan-500/20 rounded-xl flex gap-3 items-start">
                <i class="ph-bold ph-lightbulb text-cyan-400 text-lg shrink-0 mt-0.5"></i>
                <div class="text-sm text-cyan-200/90">
                  <p class="font-bold mb-1 text-cyan-300">温馨提示</p>
                  <p class="opacity-80 text-xs leading-relaxed">
                    添加物流轨迹后，用户可以在订单详情中查看完整的物流追踪信息。建议及时更新物流状态，提升用户体验。
                  </p>
                </div>
              </div>
            </div>

            <!-- 弹窗底部 -->
            <div class="p-6 border-t border-slate-700 flex justify-end gap-3 bg-slate-900/50">
              <button
                @click="closeAddTrackingModal"
                class="px-6 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 rounded-xl text-sm font-medium transition-all"
              >
                取消
              </button>
              <button
                @click="saveTracking"
                :disabled="savingLogistics"
                class="px-6 py-2.5 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white border border-cyan-500/30 rounded-xl text-sm font-bold transition-all flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-cyan-900/30"
              >
                <i v-if="!savingLogistics" class="ph-bold ph-check"></i>
                <i v-else class="ph-bold ph-circle-notch animate-spin"></i>
                {{ savingLogistics ? '保存中...' : '保存' }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 确认对话框 -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showConfirmDialog" class="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" @click.self="closeConfirmDialog">
          <div class="bg-slate-900 w-full max-w-md rounded-2xl border border-slate-700 shadow-2xl overflow-hidden">
            <!-- 对话框头部 -->
            <div 
              class="p-6 border-b border-slate-700 flex items-center gap-4"
              :class="{
                'bg-gradient-to-r from-yellow-900/20 to-slate-900': confirmDialogConfig.type === 'warning',
                'bg-gradient-to-r from-red-900/20 to-slate-900': confirmDialogConfig.type === 'danger',
                'bg-gradient-to-r from-blue-900/20 to-slate-900': confirmDialogConfig.type === 'info'
              }"
            >
              <div 
                class="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                :class="{
                  'bg-yellow-500/20 text-yellow-400': confirmDialogConfig.type === 'warning',
                  'bg-red-500/20 text-red-400': confirmDialogConfig.type === 'danger',
                  'bg-blue-500/20 text-blue-400': confirmDialogConfig.type === 'info'
                }"
              >
                <i 
                  class="text-2xl"
                  :class="{
                    'ph-bold ph-warning': confirmDialogConfig.type === 'warning',
                    'ph-bold ph-warning-circle': confirmDialogConfig.type === 'danger',
                    'ph-bold ph-info': confirmDialogConfig.type === 'info'
                  }"
                ></i>
              </div>
              <div class="flex-1">
                <h3 class="text-lg font-bold text-white">{{ confirmDialogConfig.title }}</h3>
              </div>
              <button @click="closeConfirmDialog" class="text-slate-400 hover:text-white transition-colors">
                <i class="ph-bold ph-x text-xl"></i>
              </button>
            </div>

            <!-- 对话框内容 -->
            <div class="p-6">
              <p class="text-slate-300 text-sm leading-relaxed">{{ confirmDialogConfig.message }}</p>
            </div>

            <!-- 对话框底部 -->
            <div class="p-6 border-t border-slate-700 flex gap-3 bg-slate-900/50">
              <button
                @click="closeConfirmDialog"
                class="flex-1 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 rounded-xl text-sm font-medium transition-all"
              >
                {{ confirmDialogConfig.cancelText }}
              </button>
              <button
                @click="handleConfirm"
                class="flex-1 py-2.5 rounded-xl text-sm font-bold transition-all"
                :class="{
                  'bg-gradient-to-r from-yellow-600 to-orange-600 hover:from-yellow-700 hover:to-orange-700 text-white border border-yellow-500/30': confirmDialogConfig.type === 'warning',
                  'bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white border border-red-500/30': confirmDialogConfig.type === 'danger',
                  'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white border border-blue-500/30': confirmDialogConfig.type === 'info'
                }"
              >
                {{ confirmDialogConfig.confirmText }}
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <!-- 退款申请详情弹窗 -->
    <Teleport to="body">
      <Transition name="modal-fade">
        <div v-if="showRefundDetailModal && selectedRefundOrder" class="fixed inset-0 z-[110] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" @click.self="closeRefundDetailModal">
          <div class="bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 w-full max-w-2xl rounded-2xl border border-slate-700/50 shadow-2xl overflow-hidden">
            <!-- 弹窗头部 - 渐变背景 -->
            <div class="relative bg-gradient-to-r from-orange-900/30 via-slate-800/50 to-slate-900/50 p-6 border-b border-orange-500/20">
              <div class="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZGVmcz48cGF0dGVybiBpZD0iZ3JpZCIgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiBwYXR0ZXJuVW5pdHM9InVzZXJTcGFjZU9uVXNlIj48cGF0aCBkPSJNIDQwIDAgTCAwIDAgMCA0MCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMDMpIiBzdHJva2Utd2lkdGg9IjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjZ3JpZCkiLz48L3N2Zz4=')] opacity-30"></div>
              <div class="relative flex justify-between items-start">
                <div class="flex items-start gap-4">
                  <div class="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500/20 to-red-500/20 border border-orange-500/30 flex items-center justify-center shadow-lg">
                    <i class="ph-bold ph-warning-circle text-2xl text-orange-400"></i>
                  </div>
                  <div>
                    <h3 class="text-white text-xl font-bold flex items-center gap-2">
                      退款申请详情
                      <span 
                        class="px-2.5 py-1 rounded-lg text-xs font-bold border"
                        :class="{
                          'bg-yellow-900/20 text-yellow-300 border-yellow-500/30': selectedRefundOrder.refundStatus === 'pending',
                          'bg-green-900/20 text-green-300 border-green-500/30': selectedRefundOrder.refundStatus === 'approved',
                          'bg-red-900/20 text-red-300 border-red-500/30': selectedRefundOrder.refundStatus === 'rejected',
                          'bg-blue-900/20 text-blue-300 border-blue-500/30': selectedRefundOrder.refundStatus === 'processing',
                          'bg-emerald-900/20 text-emerald-300 border-emerald-500/30': selectedRefundOrder.refundStatus === 'completed'
                        }"
                      >
                        {{ getRefundStatusLabel(selectedRefundOrder.refundStatus) }}
                      </span>
                    </h3>
                    <p class="text-slate-400 text-sm font-mono mt-1">订单号: {{ selectedRefundOrder.orderNumber }}</p>
                  </div>
                </div>
                <button @click="closeRefundDetailModal" class="text-slate-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-lg">
                  <i class="ph-bold ph-x text-xl"></i>
                </button>
              </div>
            </div>

            <!-- 弹窗内容 -->
            <div class="p-6 space-y-5 max-h-[calc(90vh-180px)] overflow-y-auto custom-scrollbar">
              <!-- 退款金额和时间 - 合并为一行 -->
              <div class="grid grid-cols-2 gap-4">
                <!-- 退款金额 -->
                <div class="relative rounded-xl p-5 border border-orange-500/20 bg-gradient-to-br from-orange-900/10 via-slate-900/50 to-red-900/10 overflow-hidden flex flex-col">
                  <div class="absolute top-0 right-0 w-24 h-24 bg-orange-500/10 rounded-full blur-2xl"></div>
                  <div class="relative z-10 flex-1 flex flex-col">
                    <div class="flex items-center gap-2 mb-3">
                      <i class="ph-bold ph-currency-circle-dollar text-orange-400"></i>
                      <span class="text-slate-400 text-xs font-bold uppercase tracking-wider">退款金额</span>
                    </div>
                    <div class="flex items-baseline gap-1.5 mt-auto">
                      <span class="text-slate-400 text-xl font-bold">¥</span>
                      <span class="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-300 via-red-300 to-orange-400 leading-tight" style="font-family: 'Arial Black', sans-serif;">
                        {{ selectedRefundOrder.refundAmount?.toFixed(2) || '0.00' }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- 时间信息 -->
                <div class="p-5 bg-slate-800/50 rounded-xl border border-slate-700/30 flex flex-col">
                  <div class="flex-1">
                    <div class="flex items-center gap-2 mb-3">
                      <i class="ph-bold ph-calendar-plus text-blue-400"></i>
                      <span class="text-slate-400 text-xs font-bold uppercase tracking-wider">申请时间</span>
                    </div>
                    <div class="font-mono">
                      <p class="text-slate-100 text-xl font-bold tracking-tight leading-tight">
                        {{ formatDate(selectedRefundOrder.refundCreatedAt) }}
                      </p>
                      <p class="text-slate-300 text-sm font-semibold tracking-wide mt-1 leading-tight">
                        {{ formatTime(selectedRefundOrder.refundCreatedAt) }}
                      </p>
                    </div>
                  </div>
                  <!-- 处理时间 - 如果存在 -->
                  <div v-if="selectedRefundOrder.refundProcessedAt" class="mt-4 pt-4 border-t border-slate-700/50">
                    <div class="flex items-center gap-2 mb-2">
                      <i class="ph-bold ph-calendar-check text-green-400 text-xs"></i>
                      <span class="text-slate-500 text-xs font-bold uppercase">处理时间</span>
                    </div>
                    <div class="font-mono">
                      <p class="text-slate-200 text-base font-bold tracking-tight leading-tight">
                        {{ formatDate(selectedRefundOrder.refundProcessedAt) }}
                      </p>
                      <p class="text-slate-400 text-xs font-medium tracking-wide mt-0.5 leading-tight">
                        {{ formatTime(selectedRefundOrder.refundProcessedAt) }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- 退款原因 - 紧凑卡片 -->
              <div class="p-4 bg-slate-800/50 rounded-xl border border-slate-700/30">
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
                    <i class="ph-bold ph-chat-circle-text text-blue-400"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 mb-2">
                      <span class="text-slate-400 text-xs font-bold uppercase tracking-wider">退款原因</span>
                      <span class="px-2.5 py-0.5 bg-blue-500/10 border border-blue-500/30 rounded text-blue-300 text-xs font-bold">
                        {{ getRefundReasonLabel(selectedRefundOrder.refundReasonType) }}
                      </span>
                    </div>
                    <p class="text-slate-300 text-sm leading-relaxed" v-if="selectedRefundOrder.refundReasonDescription">
                      {{ selectedRefundOrder.refundReasonDescription }}
                    </p>
                    <p class="text-slate-500 text-sm italic" v-else>用户未填写详细说明</p>
                  </div>
                </div>
              </div>

              <!-- 管理员备注 - 仅在有备注时显示 -->
              <div v-if="selectedRefundOrder.refundAdminRemark" class="p-4 bg-red-900/10 rounded-xl border border-red-500/20">
                <div class="flex items-start gap-3">
                  <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-red-500/10 border border-red-500/30 flex items-center justify-center">
                    <i class="ph-bold ph-warning text-red-400"></i>
                  </div>
                  <div class="flex-1 min-w-0">
                    <div class="text-slate-400 text-xs font-bold uppercase tracking-wider mb-2">管理员备注</div>
                    <p class="text-red-300 text-sm leading-relaxed">{{ selectedRefundOrder.refundAdminRemark }}</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- 弹窗底部操作按钮 -->
            <div class="p-5 border-t border-slate-700/50 bg-slate-900/50 flex gap-3">
              <button
                v-if="selectedRefundOrder.refundStatus === 'pending' || selectedRefundOrder.refundStatus === 'approved'"
                @click="approveRefundFromModal"
                class="flex-1 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white border border-green-500/30 rounded-xl text-sm font-bold hover:from-green-700 hover:to-emerald-700 flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-green-900/30"
              >
                <i class="ph-bold ph-check-circle text-lg"></i>
                同意退款
              </button>
              <button
                v-if="selectedRefundOrder.refundStatus === 'pending' || selectedRefundOrder.refundStatus === 'approved'"
                @click="rejectRefundFromModal"
                class="flex-1 py-3 bg-red-600/20 text-red-300 border border-red-500/30 rounded-xl text-sm font-bold hover:bg-red-600/30 flex items-center justify-center gap-2 transition-all active:scale-95 shadow-lg shadow-red-900/20"
              >
                <i class="ph-bold ph-x-circle text-lg"></i>
                驳回申请
              </button>
              <button
                v-if="selectedRefundOrder.refundStatus !== 'pending' && selectedRefundOrder.refundStatus !== 'approved'"
                @click="closeRefundDetailModal"
                class="flex-1 py-3 bg-slate-700/50 text-slate-300 border border-slate-600/30 rounded-xl text-sm font-bold hover:bg-slate-700/70 flex items-center justify-center gap-2 transition-all active:scale-95"
              >
                <i class="ph-bold ph-x text-lg"></i>
                关闭
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { shopApi } from '@/api/shop';
import { useOpsToast } from '@/composables/ops/useOpsToast';

const { showSuccessToast, showErrorToast } = useOpsToast();

const currentFilter = ref('all');
const searchQuery = ref('');
const loading = ref(false);
const orders = ref([]);
const showDetailModal = ref(false);
const selectedOrder = ref(null);
const showRefundDetailModal = ref(false);
const selectedRefundOrder = ref(null);
const showEditLogisticsModal = ref(false);
const editingOrder = ref(null);
const savingLogistics = ref(false);
const courierDropdownOpen = ref(false);

// 更新物流轨迹相关
const showAddTrackingModal = ref(false);
const showTrackingTimePicker = ref(false);
const trackingDateTime = ref({
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  day: new Date().getDate(),
  hour: new Date().getHours(),
  minute: new Date().getMinutes()
});
const trackingForm = ref({
  status: '',
  location: '',
  trackingTime: ''
});

const logisticsForm = ref({
  status: '',
  courierType: '',
  trackingNumber: '',
  currentLocation: '',
  statusDescription: '',
  estimatedDelivery: '',
  remark: ''
});

// 日期时间选择器相关
const showDateTimePicker = ref(false);
const tempDateTime = ref({
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  day: new Date().getDate(),
  hour: 12,
  minute: 0
});

// 验证数字输入（只允许输入数字）
const validateNumberInput = (event, field, maxLength) => {
  let value = event.target.value;
  // 只保留数字
  value = value.replace(/[^\d]/g, '');
  // 限制长度
  if (value.length > maxLength) {
    value = value.slice(0, maxLength);
  }
  // 更新值
  trackingDateTime.value[field] = value ? parseInt(value) : '';
  // 更新输入框显示
  event.target.value = value;
};

// 只允许输入数字（keypress事件）
const onlyNumbers = (event) => {
  const charCode = event.which ? event.which : event.keyCode;
  // 只允许数字键（0-9）
  if (charCode < 48 || charCode > 57) {
    event.preventDefault();
  }
};

// 格式化日期时间显示
const formatDateTimeDisplay = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  const date = new Date(dateTimeStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year} 年 ${month} 月 ${day} 日  ${hour}:${minute}`;
};

// 设置快捷日期（n天后）
const setQuickDateTime = (days) => {
  const date = new Date();
  date.setDate(date.getDate() + days);
  tempDateTime.value = {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: 12,
    minute: 0
  };
};

// 确认日期时间
// 确认日期时间
const confirmDateTime = () => {
  // 转换为数字并验证
  const year = parseInt(tempDateTime.value.year) || new Date().getFullYear();
  const month = Math.max(1, Math.min(12, parseInt(tempDateTime.value.month) || 1));
  const day = Math.max(1, Math.min(31, parseInt(tempDateTime.value.day) || 1));
  const hour = Math.max(0, Math.min(23, parseInt(tempDateTime.value.hour) || 0));
  const minute = Math.max(0, Math.min(59, parseInt(tempDateTime.value.minute) || 0));
  
  // 更新tempDateTime为验证后的值
  tempDateTime.value = { year, month, day, hour, minute };
  
  const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  logisticsForm.value.estimatedDelivery = dateStr;
  showDateTimePicker.value = false;
};

// 清除日期时间
const clearDateTime = () => {
  logisticsForm.value.estimatedDelivery = '';
  showDateTimePicker.value = false;
};

const courierOptions = [
  { id: 'sf', name: '顺丰速运', en: 'SF Express', eta: '次日达', logo: '/images/courier services/SF.png' },
  { id: 'jdl', name: '京东物流', en: 'JD Logistics', eta: '次日达', logo: '/images/courier services/JDL.png' },
  { id: 'cn-post', name: '中国邮政', en: 'China Post', eta: '全国可送', logo: '/images/courier services/CN POST.png' },
  { id: 'yto', name: '圆通速递', en: 'YTO Express', eta: '2-3天', logo: '/images/courier services/YTO.png' },
  { id: 'yt', name: '圆通速递', en: 'YTO Express', eta: '2-3天', logo: '/images/courier services/YTO.png' },
  { id: 'yunda', name: '韵达快递', en: 'Yunda Express', eta: '2-3天', logo: '/images/courier services/YDO.png' },
  { id: 'yd', name: '韵达快递', en: 'Yunda Express', eta: '2-3天', logo: '/images/courier services/YDO.png' },
  { id: 'zto', name: '中通快递', en: 'ZTO Express', eta: '2-3天', logo: '/images/courier services/ZTO.png' },
  // 支持中文名称作为ID（用于兼容旧数据）
  { id: '顺丰速运', name: '顺丰速运', en: 'SF Express', eta: '次日达', logo: '/images/courier services/SF.png' },
  { id: '京东物流', name: '京东物流', en: 'JD Logistics', eta: '次日达', logo: '/images/courier services/JDL.png' },
  { id: '京东快递', name: '京东物流', en: 'JD Logistics', eta: '次日达', logo: '/images/courier services/JDL.png' },
  { id: '中国邮政', name: '中国邮政', en: 'China Post', eta: '全国可送', logo: '/images/courier services/CN POST.png' },
  { id: '圆通速递', name: '圆通速递', en: 'YTO Express', eta: '2-3天', logo: '/images/courier services/YTO.png' },
  { id: '韵达快递', name: '韵达快递', en: 'Yunda Express', eta: '2-3天', logo: '/images/courier services/YDO.png' },
  { id: '中通快递', name: '中通快递', en: 'ZTO Express', eta: '2-3天', logo: '/images/courier services/ZTO.png' }
];

// 用于下拉框显示的去重列表（只显示英文ID的选项）
const courierDisplayOptions = [
  { id: 'sf', name: '顺丰速运', en: 'SF Express', eta: '次日达', logo: '/images/courier services/SF.png' },
  { id: 'jdl', name: '京东物流', en: 'JD Logistics', eta: '次日达', logo: '/images/courier services/JDL.png' },
  { id: 'cn-post', name: '中国邮政', en: 'China Post', eta: '全国可送', logo: '/images/courier services/CN POST.png' },
  { id: 'yto', name: '圆通速递', en: 'YTO Express', eta: '2-3天', logo: '/images/courier services/YTO.png' },
  { id: 'yunda', name: '韵达快递', en: 'Yunda Express', eta: '2-3天', logo: '/images/courier services/YDO.png' },
  { id: 'zto', name: '中通快递', en: 'ZTO Express', eta: '2-3天', logo: '/images/courier services/ZTO.png' }
];

// 根据选中的订单状态判断应该显示哪些字段
const shouldShowLogisticsFields = computed(() => {
  const status = logisticsForm.value.status;
  return status === 'shipped' || status === 'delivered';
});
const allStatuses = [
  { id: 'pending', label: '待支付', icon: 'ph-bold ph-clock', order: 0 },
  { id: 'paid', label: '已支付', icon: 'ph-bold ph-check-circle', order: 1 },
  { id: 'shipped', label: '已发货', icon: 'ph-bold ph-truck', order: 2 },
  { id: 'delivered', label: '已签收', icon: 'ph-bold ph-package-check', order: 3 },
  { id: 'cancelled', label: '已取消', icon: 'ph-bold ph-x-circle', order: 99 },
  { id: 'refunded', label: '已退款', icon: 'ph-bold ph-arrow-counter-clockwise', order: 99 }
];

// 状态流程顺序映射
const statusOrderMap = {
  'pending': 0,   // 待支付
  'paid': 1,      // 已支付
  'shipped': 2,   // 已发货
  'delivered': 3, // 已签收
  'cancelled': 99, // 已取消（终态）
  'refunded': 99   // 已退款（终态）
};

// 判断状态是否可选
const isStatusDisabled = (targetStatus) => {
  if (!editingOrder.value) return true;
  
  const currentStatus = editingOrder.value.status;
  const currentOrder = statusOrderMap[currentStatus] || 0;
  const targetOrder = statusOrderMap[targetStatus] || 0;
  
  // 终态（已取消、已退款）不能再修改
  if (currentOrder === 99) return true;
  
  // 禁用当前状态（不能选择自己）
  if (targetStatus === currentStatus) return true;
  
  // 特殊逻辑：已支付状态只能选择"已发货"（标记发货操作）
  if (currentStatus === 'paid') {
    return targetStatus !== 'shipped';
  }
  
  // 之前的状态不可选
  if (targetOrder < currentOrder && targetOrder !== 99) return true;
  
  // 特殊规则：
  // 1. 待支付只能到已支付或已取消
  if (currentStatus === 'pending' && targetStatus !== 'paid' && targetStatus !== 'cancelled') {
    return true;
  }
  
  // 2. 已发货可以到已签收、已退款
  if (currentStatus === 'shipped' && !['delivered', 'refunded'].includes(targetStatus)) {
    return true;
  }
  
  // 3. 已签收只能到已退款
  if (currentStatus === 'delivered' && targetStatus !== 'refunded') {
    return true;
  }
  
  return false;
};

// 获取状态标签
const getStatusLabel = (status) => {
  const statusObj = allStatuses.find(s => s.id === status);
  return statusObj ? statusObj.label : status;
};

// 获取退款状态标签
const getRefundStatusLabel = (refundStatus) => {
  const refundStatusMap = {
    pending: '待处理',
    approved: '已同意',
    rejected: '已驳回',
    processing: '处理中',
    completed: '已完成',
    cancelled: '已取消'
  };
  return refundStatusMap[refundStatus] || refundStatus;
};

const filters = [
  { id: 'all', label: '全部订单' },
  { id: 'pending', label: '待支付' },
  { id: 'paid', label: '已支付' },
  { id: 'shipped', label: '已发货' },
  { id: 'delivered', label: '已签收' },
  { id: 'cancelled', label: '已取消' },
  { id: 'refunded', label: '已退款' }
];

const statusMap = {
  pending: { 
    label: '待支付', 
    color: 'bg-yellow-900/20 text-yellow-300 border-yellow-500/30',
    icon: 'ph-bold ph-clock'
  },
  paid: { 
    label: '已支付', 
    color: 'bg-blue-900/20 text-blue-300 border-blue-500/30',
    icon: 'ph-bold ph-check-circle'
  },
  shipped: { 
    label: '已发货', 
    color: 'bg-purple-900/20 text-purple-300 border-purple-500/30',
    icon: 'ph-bold ph-truck'
  },
  delivered: { 
    label: '已签收', 
    color: 'bg-green-900/20 text-green-300 border-green-500/30',
    icon: 'ph-bold ph-package-check'
  },
  cancelled: { 
    label: '已取消', 
    color: 'bg-gray-800/50 text-gray-400 border-gray-600/30',
    icon: 'ph-bold ph-x-circle'
  },
  refunded: { 
    label: '已退款', 
    color: 'bg-red-900/20 text-red-300 border-red-500/30',
    icon: 'ph-bold ph-arrow-counter-clockwise'
  }
};

const filteredOrders = computed(() => {
  let result = orders.value;
  
  // 状态筛选
  if (currentFilter.value !== 'all') {
    result = result.filter(order => order.status === currentFilter.value);
  }
  
  // 搜索筛选（前端过滤，提升响应速度）
  if (searchQuery.value.trim()) {
    const query = searchQuery.value.trim().toLowerCase();
    result = result.filter(order => 
      order.orderNumber.toLowerCase().includes(query) ||
      order.userId.toString().includes(query) ||
      order.productName.toLowerCase().includes(query) ||
      (order.buyerCompanyName && order.buyerCompanyName.toLowerCase().includes(query)) ||
      (order.buyerRealName && order.buyerRealName.toLowerCase().includes(query))
    );
  }
  
  return result;
});

// 分页相关
const currentPage = ref(1);
const pageSize = 10;

// 总页数
const totalPages = computed(() => Math.ceil(filteredOrders.value.length / pageSize));

// 当前页的订单
const paginatedOrders = computed(() => {
  const start = (currentPage.value - 1) * pageSize;
  const end = start + pageSize;
  return filteredOrders.value.slice(start, end);
});

// 切换页码
const goToPage = (page) => {
  if (page < 1 || page > totalPages.value) return;
  currentPage.value = page;
  // 滚动到顶部
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// 生成页码数组（智能省略）
const getPageNumbers = () => {
  const pages = [];
  const total = totalPages.value;
  const current = currentPage.value;
  
  if (total <= 7) {
    // 总页数小于等于7，全部显示
    for (let i = 1; i <= total; i++) {
      pages.push(i);
    }
  } else {
    // 总页数大于7，智能省略
    if (current <= 3) {
      // 当前页在前面
      pages.push(1, 2, 3, 4, 5, '...', total);
    } else if (current >= total - 2) {
      // 当前页在后面
      pages.push(1, '...', total - 4, total - 3, total - 2, total - 1, total);
    } else {
      // 当前页在中间
      pages.push(1, '...', current - 1, current, current + 1, '...', total);
    }
  }
  
  return pages;
};

// 监听筛选条件变化，重置到第一页
watch([currentFilter, searchQuery], () => {
  currentPage.value = 1;
});

const loadOrders = async () => {
  loading.value = true;
  try {
    // 加载所有订单，状态筛选由前端computed处理
    const response = await shopApi.getAllOrders('all', '');
    orders.value = response.data || [];
  } catch (error) {
    console.error('加载订单失败:', error);
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  // 搜索由computed自动处理，这里可以添加防抖逻辑
};

// 确认对话框相关
const showConfirmDialog = ref(false);
const confirmDialogConfig = ref({
  title: '',
  message: '',
  confirmText: '确定',
  cancelText: '取消',
  onConfirm: null,
  type: 'warning' // warning, danger, info
});

const openConfirmDialog = (config) => {
  confirmDialogConfig.value = {
    title: config.title || '确认操作',
    message: config.message || '确定要执行此操作吗？',
    confirmText: config.confirmText || '确定',
    cancelText: config.cancelText || '取消',
    onConfirm: config.onConfirm,
    type: config.type || 'warning'
  };
  showConfirmDialog.value = true;
};

const closeConfirmDialog = () => {
  showConfirmDialog.value = false;
  confirmDialogConfig.value.onConfirm = null;
};

const handleConfirm = () => {
  if (confirmDialogConfig.value.onConfirm) {
    confirmDialogConfig.value.onConfirm();
  }
  closeConfirmDialog();
};

const cancelOrder = async (orderId) => {
  openConfirmDialog({
    title: '取消订单',
    message: '确认取消此订单？取消后无法恢复。',
    confirmText: '确认取消',
    type: 'danger',
    onConfirm: async () => {
      try {
        await shopApi.updateOrderStatus(orderId, 'cancelled');
        await loadOrders();
        showSuccessToast('订单已取消');
      } catch (error) {
        console.error('取消订单失败:', error);
        showErrorToast('操作失败：' + (error.response?.data?.message || error.message));
      }
    }
  });
};

const refundOrder = async (orderId) => {
  openConfirmDialog({
    title: '退款订单',
    message: '确认对此订单进行退款？退款后将返还用户支付金额。',
    confirmText: '确认退款',
    type: 'warning',
    onConfirm: async () => {
      try {
        await shopApi.updateOrderStatus(orderId, 'refunded');
        await loadOrders();
        showSuccessToast('订单已退款');
      } catch (error) {
        console.error('退款失败:', error);
        showErrorToast('操作失败：' + (error.response?.data?.message || error.message));
      }
    }
  });
};

// 查看退款申请详情
const viewRefundDetail = (order) => {
  selectedRefundOrder.value = order;
  showRefundDetailModal.value = true;
};

// 关闭退款详情弹窗
const closeRefundDetailModal = () => {
  showRefundDetailModal.value = false;
  selectedRefundOrder.value = null;
};

// 从详情弹窗同意退款
const approveRefundFromModal = () => {
  if (selectedRefundOrder.value) {
    closeRefundDetailModal();
    approveRefund(selectedRefundOrder.value);
  }
};

// 从详情弹窗驳回退款
const rejectRefundFromModal = () => {
  if (selectedRefundOrder.value) {
    closeRefundDetailModal();
    rejectRefund(selectedRefundOrder.value);
  }
};

// 获取退款原因标签
const getRefundReasonLabel = (reasonType) => {
  const reasonMap = {
    'doa': '质量问题',
    'firmware': '固件问题',
    'damaged': '运输损坏',
    'wrong': '拍错/发错货',
    'performance': '性能问题',
    'compatibility': '兼容性问题',
    'missing': '配件缺失',
    'other': '其他原因'
  };
  return reasonMap[reasonType] || reasonType || '未知原因';
};

// 同意退款
const approveRefund = async (order) => {
  openConfirmDialog({
    title: '同意退款申请',
    message: `确认同意订单 ${order.orderNumber} 的退款申请吗？\n\n退款金额：¥${order.refundAmount?.toFixed(2) || '0.00'}\n退款原因：${getRefundReasonLabel(order.refundReasonType)}\n\n同意后，订单状态将变更为"已退款"，此操作不可撤销。`,
    confirmText: '确认同意',
    type: 'warning',
    onConfirm: async () => {
      try {
        await shopApi.approveRefund(order.id);
        await loadOrders();
        showSuccessToast('已同意退款申请，订单已标记为已退款');
      } catch (error) {
        console.error('同意退款失败:', error);
        showErrorToast('操作失败：' + (error.response?.data?.message || error.message));
      }
    }
  });
};

// 驳回退款
const rejectRefund = async (order) => {
  openConfirmDialog({
    title: '驳回退款申请',
    message: `确认驳回订单 ${order.orderNumber} 的退款申请吗？\n\n退款金额：¥${order.refundAmount?.toFixed(2) || '0.00'}\n退款原因：${getRefundReasonLabel(order.refundReasonType)}\n\n驳回后，用户可以再次提交退款申请。`,
    confirmText: '确认驳回',
    type: 'danger',
    onConfirm: async () => {
      try {
        await shopApi.rejectRefund(order.id, '管理员驳回');
        await loadOrders();
        showSuccessToast('已驳回退款申请');
      } catch (error) {
        console.error('驳回退款失败:', error);
        showErrorToast('操作失败：' + (error.response?.data?.message || error.message));
      }
    }
  });
};

const openEditLogisticsModal = (order) => {
  editingOrder.value = order;
  
  // 如果是已支付状态，自动设置为已发货（标记发货操作）
  // 如果是其他状态（已发货/已签收），不设置默认状态，让用户选择
  const targetStatus = order.status === 'paid' ? 'shipped' : '';
  
  logisticsForm.value = {
    status: targetStatus,
    courierType: order.courierType || '',
    trackingNumber: order.trackingNumber || '',
    currentLocation: '',
    statusDescription: '',
    estimatedDelivery: '',
    remark: order.remark || ''
  };
  
  // 初始化日期时间选择器（默认为明天中午12点）
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tempDateTime.value = {
    year: tomorrow.getFullYear(),
    month: tomorrow.getMonth() + 1,
    day: tomorrow.getDate(),
    hour: 12,
    minute: 0
  };
  
  showEditLogisticsModal.value = true;
};

const closeEditLogisticsModal = () => {
  showEditLogisticsModal.value = false;
  editingOrder.value = null;
  courierDropdownOpen.value = false;
  logisticsForm.value = {
    status: '',
    courierType: '',
    trackingNumber: '',
    currentLocation: '',
    statusDescription: '',
    estimatedDelivery: '',
    remark: ''
  };
};

const selectCourier = (courierId) => {
  logisticsForm.value.courierType = courierId;
  courierDropdownOpen.value = false;
};

const getCourierName = (courierId) => {
  if (!courierId) return '';
  const courier = courierOptions.find(c => c.id === courierId);
  return courier ? courier.name : courierId; // 如果找不到，返回原始ID
};

// 获取商品图片
const getProductImage = (productName) => {
  if (!productName) return '/images/shop/No1.png';
  
  // 主商品图片映射
  if (productName.includes('SmartGrid Pro') || productName.includes('大功率')) {
    return '/images/shop/No1.png';
  }
  if (productName.includes('SmartGrid Lite') || productName.includes('小功率')) {
    return '/images/shop/No2.png';
  }
  
  // 配件图片映射
  if (productName.includes('100A') || productName.includes('互感器')) {
    return '/images/shop/n01.png';
  }
  if (productName.includes('3dB') || productName.includes('天线')) {
    return '/images/shop/n02.png';
  }
  if (productName.includes('Type-C') || productName.includes('测试线')) {
    return '/images/shop/n03.png';
  }
  if (productName.includes('86盒') || productName.includes('支架')) {
    return '/images/shop/n04.png';
  }
  if (productName.includes('消闪烁') || productName.includes('电容')) {
    return '/images/shop/n05.png';
  }
  
  // 默认图片
  return '/images/shop/No1.png';
};

const getCourierEn = (courierId) => {
  if (!courierId) return '';
  const courier = courierOptions.find(c => c.id === courierId);
  return courier ? courier.en : '';
};

const getCourierLogo = (courierId) => {
  if (!courierId) return '';
  const courier = courierOptions.find(c => c.id === courierId);
  return courier ? courier.logo : '';
};

const handleImageError = (event) => {
  // 如果图片加载失败，使用一个SVG作为备用
  event.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHJ4PSI4IiBmaWxsPSIjMUUyOTNCIi8+CiAgPHBhdGggZD0iTTEyIDI0TDIwIDI4TDI4IDI0VjE2TDIwIDEyTDEyIDE2VjI0WiIgc3Ryb2tlPSIjNjM3M0E4IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgogIDxwYXRoIGQ9Ik0xMiAxNkwyMCAyMEwyOCAxNiIgc3Ryb2tlPSIjNjM3M0E4IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgogIDxwYXRoIGQ9Ik0yMCAyMFYyOCIgc3Ryb2tlPSIjNjM3M0E4IiBzdHJva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4=';
};

const saveLogistics = async () => {
  if (!editingOrder.value) return;
  
  // 验证必填字段
  if (!logisticsForm.value.status) {
    showErrorToast('请选择订单状态');
    return;
  }
  
  if (logisticsForm.value.status === 'shipped' && !logisticsForm.value.courierType) {
    showErrorToast('已发货状态需要选择快递公司');
    return;
  }
  
  if (logisticsForm.value.status === 'shipped' && !logisticsForm.value.trackingNumber) {
    showErrorToast('已发货状态需要填写快递单号');
    return;
  }
  
  savingLogistics.value = true;
  try {
    // 调用新的物流更新API
    await shopApi.updateOrderLogistics(editingOrder.value.id, logisticsForm.value);
    
    showSuccessToast('物流信息更新成功');
    closeEditLogisticsModal();
    await loadOrders();
  } catch (error) {
    console.error('更新物流信息失败:', error);
    showErrorToast('操作失败：' + (error.response?.data?.message || error.message));
  } finally {
    savingLogistics.value = false;
  }
};

const viewDetail = (order) => {
  selectedOrder.value = order;
  showDetailModal.value = true;
};

const formatDateTime = (dateTime) => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const formatDate = (dateTime) => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

const formatTime = (dateTime) => {
  if (!dateTime) return '-';
  const date = new Date(dateTime);
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  const second = String(date.getSeconds()).padStart(2, '0');
  return `${hour}:${minute}:${second}`;
};

const getPaymentMethodName = (method) => {
  const methodMap = {
    'wechat': '微信支付',
    'alipay': '支付宝',
    'unionpay': '银联支付',
    'cash': '货到付款'
  };
  return methodMap[method] || method || '-';
};

// 打开更新物流轨迹弹窗
const openAddTrackingModal = (order) => {
  editingOrder.value = order;
  
  // 初始化跟踪时间选择器为当前时间
  const now = new Date();
  trackingDateTime.value = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
    hour: now.getHours(),
    minute: now.getMinutes()
  };
  
  // 设置表单的默认时间（ISO格式）
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  const hour = String(now.getHours()).padStart(2, '0');
  const minute = String(now.getMinutes()).padStart(2, '0');
  
  trackingForm.value = {
    status: '',
    location: '',
    trackingTime: `${year}-${month}-${day}T${hour}:${minute}`
  };
  
  showAddTrackingModal.value = true;
};

// 格式化显示日期时间
const formatDisplayDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  const date = new Date(dateTimeStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hour = String(date.getHours()).padStart(2, '0');
  const minute = String(date.getMinutes()).padStart(2, '0');
  return `${year}年${month}月${day}日 ${hour}:${minute}`;
};

// 设置跟踪时间为现在
const setTrackingTimeToNow = () => {
  const now = new Date();
  trackingDateTime.value = {
    year: now.getFullYear(),
    month: now.getMonth() + 1,
    day: now.getDate(),
    hour: now.getHours(),
    minute: now.getMinutes()
  };
};

// 确认跟踪时间
const confirmTrackingTime = () => {
  const { year, month, day, hour, minute } = trackingDateTime.value;
  
  // 验证输入
  if (!year || !month || !day || hour === '' || minute === '') {
    showErrorToast('请填写完整的日期和时间');
    return;
  }
  
  // 构建日期时间字符串
  const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  trackingForm.value.trackingTime = dateStr;
  showTrackingTimePicker.value = false;
};

// 关闭更新物流轨迹弹窗
const closeAddTrackingModal = () => {
  showAddTrackingModal.value = false;
  showTrackingTimePicker.value = false;
  editingOrder.value = null;
  trackingForm.value = {
    status: '',
    location: '',
    trackingTime: ''
  };
};

// 保存物流轨迹
const saveTracking = async () => {
  // 验证必填字段
  if (!trackingForm.value.status) {
    showErrorToast('请输入状态描述');
    return;
  }
  
  if (!trackingForm.value.trackingTime) {
    showErrorToast('请选择跟踪时间');
    return;
  }
  
  try {
    savingLogistics.value = true;
    
    // 调用API添加物流轨迹
    await shopApi.addLogisticsTracking(editingOrder.value.id, trackingForm.value);
    
    showSuccessToast('物流轨迹更新成功');
    closeAddTrackingModal();
    await loadOrders();
  } catch (error) {
    console.error('更新物流轨迹失败:', error);
    showErrorToast('操作失败：' + (error.response?.data?.message || error.message));
  } finally {
    savingLogistics.value = false;
  }
};

// 从收货地址提取送达地（省市区）
const getDestinationFromAddress = (address) => {
  if (!address) return '-';
  const parts = [];
  if (address.province) parts.push(address.province);
  if (address.city) parts.push(address.city);
  if (address.district) parts.push(address.district);
  return parts.join('') || '-';
};

onMounted(() => {
  // 确保管理员用户信息存在（用于API认证）
  const user = sessionStorage.getItem('currentUser');
  if (!user) {
    // 设置一个默认的管理员用户信息
    const adminUser = {
      id: 1,
      role: 'owner',
      username: 'admin'
    };
    sessionStorage.setItem('currentUser', JSON.stringify(adminUser));
  }
  
  loadOrders();
});
</script>

<style scoped>
/* Glass panel效果 */
.glass-panel {
  background: linear-gradient(135deg, rgba(15, 23, 42, 0.7) 0%, rgba(30, 41, 59, 0.5) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

/* 隐藏数字输入框的上下箭头 */
input[type="text"]::-webkit-outer-spin-button,
input[type="text"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

/* 列表过渡动画 */
.list-enter-active,
.list-leave-active {
  transition: all 0.4s ease;
}

.list-enter-from {
  opacity: 0;
  transform: translateY(20px);
}

.list-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.list-move {
  transition: transform 0.4s ease;
}

/* 弹窗过渡动画 */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

/* 字段过渡动画 */
.field-fade-enter-active,
.field-fade-leave-active {
  transition: all 0.3s ease;
}

.field-fade-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.field-fade-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

.field-fade-move {
  transition: transform 0.3s ease;
}

/* 下拉框过渡动画 */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.2s ease;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-5px);
}

/* 向上展开的下拉框过渡动画 */
.dropdown-up-enter-active,
.dropdown-up-leave-active {
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.dropdown-up-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.dropdown-up-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

/* 数字变换动画 */
@keyframes numberChange {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.2);
    color: rgb(236, 72, 153);
  }
  100% {
    transform: scale(1);
  }
}

.number-change {
  animation: numberChange 0.3s ease-out;
}

/* 自定义滚动条 */
.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background: rgba(139, 92, 246, 0.5);
  border-radius: 3px;
}

.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: rgba(139, 92, 246, 0.7);
}
</style>
